import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setRefreshSidebar } from "../redux/sidebarSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/ChatPage.css";
import Swal from "sweetalert2";
import {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import api from "../api";
import {
  decryptIncomingChatText,
  encryptOutgoingChatText,
  loadChatSecurityContext,
} from "../services/ChatSecurityManager";
import { useChatStreamManager } from "../contexts/ChatStreamContext";
import { syncProfilesFromResponse } from "../profile/profileSync";
import {
  extractUsageCycle,
  extractTokenUsage,
  getUsageCycleRingVariant,
  getSessionLimitMessage,
  isSessionLimitError,
  isSessionLimitReached,
} from "../utils/sessionLimit";
import {
  createEmptyExecutionTrace,
  isTraceVisible,
} from "../utils/executionTrace";
import { useSessionLimitEnforcement } from "../hooks/useSessionLimitEnforcement";
import { useChatFeedback } from "../hooks/useChatFeedback";
import { useSessionUsageWarnings } from "../hooks/useSessionUsageWarnings";
import {
  setIsChatPage,
  setSessionId as setReduxSessionId,
} from "../redux/sessionSlice";
import { branding } from "../utils/branding";

// Import the new ChatMessage component
import ChatMessage from "./ChatMessage";
import { useOutletContext } from "react-router-dom";
import SessionUsageRing from "../components/SessionUsageRing";

import fileIcon from "../assets/UserIcons/FileIcon.svg";
import send from "../assets/UserIcons/SendChatIcon.svg";
import crossIcon from "../assets/UserIcons/Cancel.svg";
import { truncate, validateFileName } from "../utils/validateFileName";

import VelaKoda from "../assets/Images/LoginLogo.svg";
import customizeResIcon from "../assets/Images/Filter icon.svg";
import reloadIcon from "../assets/UserIcons/Reload.svg";
import UploadDocumentModal from "./UploadDocumentModal";

// Utility function for debouncing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const normalizeModelType = (value, fallback) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "online" || normalized === "offline") return normalized;
  return fallback;
};

const ChatPage = ({
  firstMessage,
  attachedFilesFromHome,
  profilePic,
  userName,
  // isSearchActive,
  // isKnowledgeSearchActive,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { selectedModel, selectedMode } = useSelector((state) => state.model);
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const [input, setInput] = useState("");
  const inputTextareaRef = useRef(null);
  const textareaResizeRafRef = useRef(null);
  const chatEndRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const hasInteractedRef = useRef(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isBotResponding, setIsBotResponding] = useState(false);
  // While true, a new chat session is being created via POST /chat/new-session.
  // Used to disable send triggers so users can't accidentally create multiple sessions
  // by double-clicking Send or pressing Enter multiple times during the session-creation window.
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const typingIntervalRef = useRef(null);
  const [shouldForceScroll, setShouldForceScroll] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const searchTerm = useSelector((state) => state.chat.searchTerm);
  const [micOpen, setMicOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [tempTranscript, setTempTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const currentUtteranceRef = useRef(null);

  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const isFullPrivacy = useSelector((state) => state.privacy.isFullPrivacy);
  const { isChatPage } = useSelector((state) => state.session);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isProcessingSTT, setIsProcessingSTT] = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  const location = useLocation();
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);

  const [sessionLimitBlocked, setSessionLimitBlocked] = useState(false);
  const [isSessionStatusChecking, setIsSessionStatusChecking] = useState(false);
  const [tokenUsageInfo, setTokenUsageInfo] = useState(null);
  const [usageCycleInfo, setUsageCycleInfo] = useState(null);

  const { detectSessionLimitFromResponse, handleSessionLimitReached } =
    useSessionLimitEnforcement();

  useSessionUsageWarnings({
    sessionId,
    tokenUsage: tokenUsageInfo,
    usageCycle: usageCycleInfo,
    sessionBlocked: sessionLimitBlocked,
  });

  const { submitFeedback } = useChatFeedback({ debounceMs: 800 });

  const [chatPreferences, setChatPreferences] = useState({
    font_size: "Medium",
    auto_scroll: true,
    bot_language: "English",
  });

  const fontSizeMap = {
    Small: "0.75rem", // 12px ✅
    Medium: "0.8rem", // 12.8px ✅ (still below 13)
    Large: "0.8125rem", // 13px (borderline – use only if needed)
  };

  const [activeButton, setActiveButton] = useState(""); // store "attach", "read", "mic", "search"
  // const [searchActive, setIsSearchActive] = useState(isSearchActive ?? true);
  const {
    isSearchActive,
    isKnowledgeSearchActive,
    setIsKnowledgeSearchActive,
    uploadedDocNames,
    setUploadedDocNames,
    uploadedDocumentIds,
    setUploadedDocumentIds,
    selectedGroups,
    setSelectedGroups,
    selectedFiles,
    setSelectedFiles,
    selectedKnowledgeContexts,
    setSelectedKnowledgeContexts,
    setKnowledgeContextSummary,
    chatResetToken,
    aiPreferences,
    setAiPreferences,
    resetAiPreferences,
    DEFAULT_AI_PREFERENCES,
    openPreview,
  } = useOutletContext();

  // console.log("uploadedDocNames", uploadedDocNames);

  const selectedModelRef = useRef(selectedModel);
  const isTypingRef = useRef(false);

  const botLifecycleRef = useRef({
    status: "idle", // idle | requesting | typing
  });

  const botStartTimeRef = useRef(null);
  const skipHistoryFetchRef = useRef(false);
  // ----------------------------------------------------------------------------------
  // Duplicate session creation protection (single-flight):
  //
  // Race condition being prevented:
  // - The user can trigger handleSend() twice very quickly (double-click, double-Enter,
  //   Enter+Click) before `isBotResponding` flips to true.
  // - Both handleSend() calls reach ensureSessionForSend() while `sessionId` is still null,
  //   which would otherwise fire TWO parallel POST /chat/new-session requests.
  // - Backend creates two distinct sessions -> sidebar shows duplicates.
  //
  // Solution:
  // - Keep a single in-flight promise for session creation and have all callers await it.
  // ----------------------------------------------------------------------------------
  const creatingSessionPromiseRef = useRef(null);

  // Duplicate send protection:
  // Prevents multiple concurrent executions of handleSend() regardless of UI event source.
  const sendingRef = useRef(false);
  const recoveryAttemptRef = useRef(0);
  const recoveryTimerRef = useRef(null);
  const sessionHistoryCacheRef = useRef(new Map());
  const sessionHistoryInFlightRef = useRef(new Map());
  const sessionPayloadCacheRef = useRef(new Map());
  const sessionPayloadInFlightRef = useRef(new Map());
  const activeResponseRef = useRef({
    botTypingId: null,
    chatId: null,
    sessionId: null,
    clientMessageId: null,
    fullResponse: "",
    charIndex: 0,
  });

  const setBotStatus = (status) => {
    botLifecycleRef.current.status = status;
  };

  const isBotBusy = () => botLifecycleRef.current.status !== "idle";

  const botBusy = isBotBusy();

  const clearRecovery = useCallback(() => {
    recoveryAttemptRef.current += 1;
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }

    activeResponseRef.current = {
      botTypingId: null,
      chatId: null,
      sessionId: null,
      clientMessageId: null,
      fullResponse: "",
      charIndex: 0,
    };
  }, []);

  const makeClientMessageId = useCallback(() => {
    try {
      if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
    } catch {}

    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }, []);

  useEffect(() => {
    selectedModelRef.current = selectedModel;
  }, [selectedModel]);

  useEffect(() => {
    // Clear docs when route or session changes
    setUploadedDocNames([]);
    setUploadedDocumentIds({});
  }, [sessionId, location.pathname]);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const chatStreamDebugEnabled = (() => {
    try {
      const envFlag = String(process.env.REACT_APP_CHAT_STREAM_DEBUG || "");
      if (/^(1|true|yes)$/i.test(envFlag)) return true;
    } catch {}
    try {
      return localStorage.getItem("chat_stream_debug") === "1";
    } catch {
      return false;
    }
  })();

  const chatStreamDebugLog = useCallback(
    (...args) => {
      if (!chatStreamDebugEnabled) return;
      try {
        // eslint-disable-next-line no-console
        console.debug("[CHAT_STREAM_UI]", ...args);
      } catch {}
    },
    [chatStreamDebugEnabled],
  );

  const [user, setUser] = useState({
    user_profile_pic: "",
    name: "",
    email: "",
  });
  // Loading state for chat list (controls "Loading messages..." placeholder).
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const refreshKey = useSelector((state) => state.refresh.key);

  // 🔑 Get privacy mode from Redux
  const privacy_mode = useSelector((state) => state.privacy.isFullPrivacy);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const streamManager = useChatStreamManager();

  const normalizeSessionId = useCallback((value) => {
    if (value == null) return null;
    const str = String(value).trim();
    return str ? str : null;
  }, []);

  const getSessionBlockedStorageKey = useCallback(
    (value) => {
      const normalized = normalizeSessionId(value);
      return normalized ? `chat_session_limit_blocked:${normalized}` : null;
    },
    [normalizeSessionId],
  );

  const readStoredSessionBlocked = useCallback(
    (value) => {
      const key = getSessionBlockedStorageKey(value);
      if (!key) return false;
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
      return false;
    },
    [getSessionBlockedStorageKey],
  );

  const writeStoredSessionBlocked = useCallback(
    (value, blocked) => {
      const key = getSessionBlockedStorageKey(value);
      if (!key) return;
      try {
        // Legacy session-limit locks were based on cumulative usage. Clear them
        // so a visual usage rollover cannot permanently disable this session.
        void blocked;
        localStorage.removeItem(key);
      } catch {}
    },
    [getSessionBlockedStorageKey],
  );

  const ACTIVE_STREAM_TTL_MS = 30 * 60 * 1000;

  const getActiveStreamStorageKey = useCallback(
    (value) => {
      const normalized = normalizeSessionId(value);
      return normalized ? `chat_active_stream:${normalized}` : null;
    },
    [normalizeSessionId],
  );

  const readActiveStreamState = useCallback(
    (value) => {
      const key = getActiveStreamStorageKey(value);
      if (!key) return null;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        const createdAtMs = Number(parsed.createdAtMs || 0) || 0;
        if (
          createdAtMs > 0 &&
          Date.now() - createdAtMs > ACTIVE_STREAM_TTL_MS
        ) {
          localStorage.removeItem(key);
          chatStreamDebugLog("active_stream_expired", { key });
          return null;
        }
        chatStreamDebugLog("active_stream_read", {
          key,
          session_id: parsed?.session_id || null,
          chat_id: parsed?.chat_id || null,
          client_message_id: parsed?.client_message_id || null,
          lastLen: parsed?.lastLen ?? null,
        });
        return parsed;
      } catch {
        return null;
      }
    },
    [ACTIVE_STREAM_TTL_MS, chatStreamDebugLog, getActiveStreamStorageKey],
  );

  const writeActiveStreamState = useCallback(
    (value, patch) => {
      const key = getActiveStreamStorageKey(value);
      if (!key) return;
      try {
        const existingRaw = localStorage.getItem(key);
        const existing =
          existingRaw && typeof existingRaw === "string"
            ? JSON.parse(existingRaw)
            : null;
        const next =
          existing && typeof existing === "object"
            ? { ...existing, ...(patch || {}) }
            : { ...(patch || {}) };
        if (!next.createdAtMs) next.createdAtMs = Date.now();
        next.updatedAtMs = Date.now();
        localStorage.setItem(key, JSON.stringify(next));
        chatStreamDebugLog("active_stream_write", {
          key,
          session_id: next?.session_id || null,
          chat_id: next?.chat_id || null,
          client_message_id: next?.client_message_id || null,
          lastLen: next?.lastLen ?? null,
        });
      } catch {}
    },
    [chatStreamDebugLog, getActiveStreamStorageKey],
  );

  const clearActiveStreamState = useCallback(
    (value) => {
      const key = getActiveStreamStorageKey(value);
      if (!key) return;
      try {
        localStorage.removeItem(key);
        chatStreamDebugLog("active_stream_clear", { key });
      } catch {}
    },
    [chatStreamDebugLog, getActiveStreamStorageKey],
  );

  const sameId = useCallback(
    (a, b) => {
      const na = normalizeSessionId(a);
      const nb = normalizeSessionId(b);
      return na === nb;
    },
    [normalizeSessionId],
  );

  const resolveSessionBlockedFromPayload = useCallback((payload) => {
    const root =
      payload &&
      typeof payload === "object" &&
      (payload.history ||
        payload.token_usage ||
        payload.sessionLimitBlocked != null ||
        payload.session_blocked != null ||
        payload.error)
        ? payload
        : payload?.data && typeof payload.data === "object"
          ? payload.data
          : payload;

    const explicitFlag = Boolean(
      root?.sessionLimitBlocked ??
      root?.session_limit_blocked ??
      root?.session_blocked ??
      root?.sessionBlocked ??
      root?.blocked,
    );

    const tokenUsage = extractTokenUsage(root) || extractTokenUsage(payload);
    const usageCycle = extractUsageCycle(root) || extractUsageCycle(payload);
    const reached =
      isSessionLimitError(root) ||
      (tokenUsage && isSessionLimitReached(tokenUsage));

    return {
      blocked: explicitFlag || Boolean(reached),
      tokenUsage: tokenUsage || root?.token_usage || null,
      usageCycle: usageCycle || root?.usage_cycle || null,
      sessionId: root?.session_id || payload?.session_id || null,
      message: getSessionLimitMessage(root || payload || {}),
    };
  }, []);

  const setTokenUsageInfoSafely = useCallback((incomingUsage) => {
    if (!incomingUsage || typeof incomingUsage !== "object") return;

    setTokenUsageInfo((prev) => {
      const prevObj = prev && typeof prev === "object" ? prev : null;
      const prevTotal =
        Number(prevObj?.total_tokens ?? prevObj?.totalTokens ?? 0) || 0;
      const incomingTotal =
        Number(
          incomingUsage?.total_tokens ?? incomingUsage?.totalTokens ?? 0,
        ) || 0;
      const prevLimit = Number(prevObj?.limit ?? 0) || 0;
      const incomingLimit = Number(incomingUsage?.limit ?? 0) || 0;

      const finalTotal = Math.max(prevTotal, incomingTotal);
      const finalLimit = prevLimit > 0 ? prevLimit : incomingLimit;

      return {
        ...(prevObj || {}),
        ...(incomingUsage || {}),
        total_tokens: finalTotal,
        limit: finalLimit,
      };
    });
  }, []);

  const setUsageCycleInfoSafely = useCallback((incomingCycle) => {
    if (!incomingCycle || typeof incomingCycle !== "object") return;

    setUsageCycleInfo((prev) => {
      const prevBoundary = Number(prev?.last_completed_boundary ?? 0) || 0;
      const incomingBoundary =
        Number(incomingCycle?.last_completed_boundary ?? 0) || 0;
      const prevTotal = Number(prev?.cumulative_total_tokens ?? 0) || 0;
      const incomingTotal =
        Number(incomingCycle?.cumulative_total_tokens ?? 0) || 0;

      if (prev && incomingBoundary < prevBoundary) return prev;
      if (prev && incomingBoundary === prevBoundary && incomingTotal < prevTotal) {
        return prev;
      }

      return incomingCycle;
    });
  }, []);

  const sessionUsageRing = useMemo(() => {
    const raw =
      usageCycleInfo && typeof usageCycleInfo === "object"
        ? usageCycleInfo
        : null;
    const total = Number(raw?.current_cycle_tokens ?? 0) || 0;
    const limit = Number(raw?.limit ?? 0) || 0;
    if (!(limit > 0)) return null;

    const percent = Math.min(
      100,
      Math.max(0, Number(raw?.current_cycle_percent ?? (total / limit) * 100) || 0),
    );
    const variant = sessionLimitBlocked ? "danger" : getUsageCycleRingVariant(raw);
    const currentCycle = Number(raw?.current_cycle ?? 1) || 1;

    return {
      total,
      limit,
      percent,
      variant,
      title: `Session usage cycle ${currentCycle}: ${Math.round(percent)}% (${total.toLocaleString()} / ${limit.toLocaleString()} tokens)`,
    };
  }, [sessionLimitBlocked, usageCycleInfo]);

  const fetchSessionPayload = useCallback(
    async (targetSessionId, { silent = false } = {}) => {
      if (!targetSessionId) return null;

      const cacheTtlMs = silent ? 5000 : 1200;
      const cacheKey = String(targetSessionId);
      const now = Date.now();

      const cached = sessionPayloadCacheRef.current.get(cacheKey);
      if (cached && now - cached.ts < cacheTtlMs) {
        return cached.data;
      }

      const inFlight = sessionPayloadInFlightRef.current.get(cacheKey);
      if (inFlight) return inFlight;

      try {
        const promise = (async () => {
          const response = await api.get(`/chat/session/${targetSessionId}`);
          const data = response?.data ?? null;
          sessionPayloadCacheRef.current.set(cacheKey, {
            ts: Date.now(),
            data,
          });
          return data;
        })();

        sessionPayloadInFlightRef.current.set(cacheKey, promise);
        return await promise;
      } catch (err) {
        const status = err?.response?.status;

        if (status === 401) {
          if (!silent) {
            setModalMessage("Your session has expired. Please login again.");
            setShowModal(true);
          }
          return null;
        }

        if (!silent) {
          setModalMessage("Failed to load session. Please try again.");
          setShowModal(true);
        }

        return null;
      } finally {
        sessionPayloadInFlightRef.current.delete(cacheKey);
      }
    },
    [api],
  );

  const fetchSessionHistory = useCallback(
    async (targetSessionId, { silent = false } = {}) => {
      if (!targetSessionId) return null;

      // De-dupe and throttle repeated history fetches (prevents /chat/session/:id spam).
      // This also protects against StrictMode double-invocation in dev.
      const cacheTtlMs = silent ? 5000 : 1200;
      const cacheKey = String(targetSessionId);
      const now = Date.now();
      const cached = sessionHistoryCacheRef.current.get(cacheKey);
      if (cached && now - cached.ts < cacheTtlMs) {
        return cached.data;
      }

      const inFlight = sessionHistoryInFlightRef.current.get(cacheKey);
      if (inFlight) return inFlight;

      try {
        const promise = (async () => {
          const data = await fetchSessionPayload(targetSessionId, { silent });

          if (!data || !Array.isArray(data.history)) return [];

          const serverEncryptionMode = data?.chat_encryption_mode || null;

          const formatted = data.history.map((msg) => ({
            id: msg.id,
            chat_id: msg.chat_id ?? null,
            client_message_id: msg.client_message_id ?? null,
            sender: msg.sender,
            message: msg.message,
            response_status: msg.response_status ?? null,
            time: msg.time,
            userName: msg.userName || "User",
            userPic: msg.userPic || null,

            // ✅ MODEL INFO
            model_name: msg.model_name || null,
            model_generic_name:
              msg.model_generic_name || msg.model_name || null,
            model_type: msg.model_type || "online",

            // 🔹 METRICS
            response_time: msg.response_time ?? null,
            response_record_time: msg.response_record_time ?? null,
            input_tokens: msg.input_tokens ?? null,
            output_tokens: msg.output_tokens ?? null,
            estimated_cost_usd: msg.estimated_cost_usd ?? null,
            confidence: msg.confidence ?? null,

            // ✅ FILE METADATA
            files:
              msg.files?.map((f) => ({
                name: f.name,
                size: f.size,
                type: f.type,
                url: f.url || null,
              })) || [],

            // ✅ RAG SOURCES & CITATIONS
            citations: msg.citations || [],
            rag_sources: msg.rag_sources || [],
            follow_up: msg.follow_up || [],
            bot_generated_file: msg.bot_generated_file || null,
            executionTrace:
              msg.executionTrace && typeof msg.executionTrace === "object"
                ? msg.executionTrace
                : null,
            streamSteps: Array.isArray(msg?.executionTrace?.steps)
              ? msg.executionTrace.steps
              : [],

            // ✅ Feedback hydration (persists Like/Dislike after refresh)
            feedback: msg.feedback ?? null,
            feedback_comment: msg.feedback_comment ?? null,
            feedbackSubmitting: false,
          }));

          sessionHistoryCacheRef.current.set(cacheKey, {
            ts: Date.now(),
            data: formatted,
          });
          if (
            String(serverEncryptionMode || "")
              .toLowerCase()
              .includes("end_to_end")
          ) {
            await loadChatSecurityContext();
            return await Promise.all(
              formatted.map(async (m) => ({
                ...m,
                message: await decryptIncomingChatText(m.message, {
                  modeOverride: serverEncryptionMode,
                }),
              })),
            );
          }

          return formatted;
        })();

        sessionHistoryInFlightRef.current.set(cacheKey, promise);
        return await promise;
      } catch (err) {
        const status = err?.response?.status;
        console.error("Error:", err);

        if (status === 401) {
          if (!silent) {
            setModalMessage("Your session has expired. Please login again.");
            setShowModal(true);
          }
          return null;
        }

        if (!silent) {
          setModalMessage("Failed to load chat history. Please try again.");
          setShowModal(true);
        }
        return null;
      } finally {
        sessionHistoryInFlightRef.current.delete(cacheKey);
      }
    },
    [fetchSessionPayload],
  );

  const ensureSessionForSend = useCallback(
    async (modelId) => {
      if (sessionId) return sessionId;
      if (!modelId) return null;
      if (!token) return null;

      // Single-flight: if a session creation request is already in progress, reuse it.
      // This is crucial because multiple send entry points can fire before state updates
      // (double-click, double-Enter, Enter+Click). Without this guard, we'd create multiple
      // sessions for one user intent.
      if (creatingSessionPromiseRef.current) {
        try {
          return await creatingSessionPromiseRef.current;
        } catch (err) {
          // If the shared in-flight request failed, do not start a parallel retry here.
          // Let the user retry via another explicit action; otherwise we risk overlapping
          // creators racing to set/clear `creatingSessionPromiseRef.current`.
          console.error("Failed to create session (in-flight):", err);
          return null;
        }
      }

      const knowledgeEnabled = Boolean(
        isKnowledgeSearchActive ||
        selectedGroups.length > 0 ||
        selectedFiles.length > 0 ||
        selectedKnowledgeContexts.length > 0,
      );

      let createPromise = null;
      try {
        setIsCreatingSession(true);

        createPromise = (async () => {
          const res = await api.post(
            `${process.env.REACT_APP_API_URL}/chat/new-session`,
            {
              model_id: modelId,
              mode: selectedMode,
              knowledge_search_enabled: knowledgeEnabled,
              group_ids: selectedGroups,
              knowledge_base_files: selectedFiles,
              knowledge_contexts: selectedKnowledgeContexts,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const newSessionId = res.data?.session_id;
          if (!newSessionId) throw new Error("Missing session_id");

          skipHistoryFetchRef.current = true;
          const normalizedNewSessionId = normalizeSessionId(newSessionId);
          setSessionId(normalizedNewSessionId);
          dispatch(setReduxSessionId(normalizedNewSessionId));
          dispatch(setRefreshSidebar(true));
          navigate("/user/new", {
            state: { sessionId: normalizedNewSessionId },
            replace: true,
          });

          return normalizedNewSessionId;
        })();

        creatingSessionPromiseRef.current = createPromise;
        return await createPromise;
      } catch (err) {
        console.error("Failed to create session:", err);
        return null;
      } finally {
        // Always clear single-flight state so future user actions can create a fresh session.
        if (
          createPromise &&
          creatingSessionPromiseRef.current === createPromise
        ) {
          creatingSessionPromiseRef.current = null;
        }
        setIsCreatingSession(false);
      }
    },
    [
      dispatch,
      isKnowledgeSearchActive,
      navigate,
      normalizeSessionId,
      selectedGroups,
      selectedFiles,
      selectedKnowledgeContexts,
      selectedMode,
      sessionId,
      token,
    ],
  );

  const recoverBotResponse = useCallback(
    async ({
      targetSessionId,
      userText,
      botTypingId,
      startedAt,
      maxMs = 600000,
    }) => {
      if (!targetSessionId) return;

      const attemptId = ++recoveryAttemptRef.current;
      const start = Date.now();

      setBotStatus("requesting");
      setIsBotResponding(true);

      let delayMs = 2000;

      const pollOnce = async () => {
        if (recoveryAttemptRef.current !== attemptId) return;

        const formatted = await fetchSessionHistory(targetSessionId, {
          silent: true,
        });

        if (recoveryAttemptRef.current !== attemptId) return;

        if (Array.isArray(formatted) && formatted.length > 0) {
          const normalizedUserText = String(userText ?? "").trim();
          const lastUserIndex = normalizedUserText
            ? formatted
                .map((m) =>
                  m.sender === "user" ? String(m.message ?? "").trim() : null,
                )
                .lastIndexOf(normalizedUserText)
            : -1;

          const candidate =
            lastUserIndex >= 0 ? formatted[lastUserIndex + 1] : null;

          if (
            candidate &&
            candidate.sender === "bot" &&
            String(candidate.message ?? "").trim().length > 0
          ) {
            clearRecovery();
            setMessages(formatted);
            setIsBotResponding(false);
            setBotStatus("idle");
            dispatch(setRefreshSidebar(true));
            return;
          }
        }

        const elapsed = Date.now() - start;
        if (elapsed >= maxMs) {
          clearRecovery();
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message:
                      "This is taking longer than usual. The server may still be generating the response—please wait a bit more or retry.",
                    partialMessage: "",
                    showDots: false,
                    response_record_time:
                      msg.response_record_time ??
                      (
                        (Date.now() -
                          (startedAt || msg.startedAt || Date.now())) /
                        1000
                      ).toFixed(2),
                  }
                : msg,
            ),
          );
          setIsBotResponding(false);
          setBotStatus("idle");
          dispatch(setRefreshSidebar(true));
          return;
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botTypingId
              ? {
                  ...msg,
                  message:
                    msg.message ||
                    "Connection issue detected. Trying to fetch the response…",
                  partialMessage: "",
                  showDots: true,
                }
              : msg,
          ),
        );

        // Backoff polling to reduce load while still auto-updating.
        delayMs = Math.min(Math.floor(delayMs * 1.4), 10000);
        recoveryTimerRef.current = setTimeout(pollOnce, delayMs);
      };

      pollOnce();
    },
    [dispatch, fetchSessionHistory, clearRecovery],
  );

  const toggleKbFile = useCallback(
    (source) => {
      const normalize = (v) => String(v ?? "").trim();
      const key = (v) => normalize(v).toLowerCase();
      const filename = normalize(source?.filename);
      const rawDocId = source?.document_id ?? source?.documentId ?? null;
      const document_id =
        rawDocId == null || String(rawDocId).trim() === ""
          ? null
          : String(rawDocId);
      // eslint-disable-next-line no-console
      console.log("[KB] toggleKbFile called", {
        filename,
        document_id,
        hasSetSelectedFiles: typeof setSelectedFiles === "function",
        hasSetIsKnowledgeSearchActive:
          typeof setIsKnowledgeSearchActive === "function",
      });
      if (!filename) return;

      setSelectedFiles((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const getName = (v) =>
          typeof v === "string"
            ? v
            : v && typeof v === "object"
              ? v.filename
              : "";
        const getId = (v) =>
          v && typeof v === "object" ? (v.document_id ?? null) : null;

        const targetKey = key(filename);
        const map = new Map();
        for (const v of prevArr) {
          const name = normalize(getName(v));
          const k = key(name);
          if (!k) continue;
          if (!map.has(k)) {
            const existingId = getId(v);
            map.set(k, {
              filename: name,
              document_id:
                existingId == null || String(existingId).trim() === ""
                  ? null
                  : String(existingId),
            });
          }
        }
        const had = map.has(targetKey);
        if (had) map.delete(targetKey);
        else {
          const existing = map.get(targetKey) || null;
          map.set(targetKey, {
            filename,
            document_id: document_id ?? existing?.document_id ?? null,
          });
        }
        const next = Array.from(map.values());

        // eslint-disable-next-line no-console
        console.log("[KB] toggle file", { filename, had, next });

        return next;
      });
    },
    [setSelectedFiles],
  );

  const createNewChatFromKb = useCallback(async () => {
    if (!selectedModel) {
      showErrorToast("Select a model first");
      return;
    }

    // Prevent accidental double-creation from rapid clicks/taps.
    if (isCreatingSession) return;

    const knowledgeEnabled = Boolean(
      isKnowledgeSearchActive ||
      selectedGroups.length > 0 ||
      selectedFiles.length > 0 ||
      selectedKnowledgeContexts.length > 0,
    );

    try {
      setIsCreatingSession(true);
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/chat/new-session`,
        {
          model_id: selectedModel,
          mode: selectedMode,
          knowledge_search_enabled: knowledgeEnabled,
          group_ids: selectedGroups,
          knowledge_base_files: selectedFiles,
          knowledge_contexts: selectedKnowledgeContexts,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const newSessionId = res.data?.session_id;
      if (!newSessionId) throw new Error("Missing session_id");
      const normalizedNewSessionId = normalizeSessionId(newSessionId);

      try {
        sessionStorage.setItem("kb_auto_open_dropdown", "1");
        sessionStorage.setItem("kb_keep_selection_for_next_session", "1");
      } catch {}

      setMessages([]);
      setInput("");
      setAttachedFiles([]);
      setUploadedDocNames([]);
      setEditingId(null);
      setIsBotResponding(false);
      setBotStatus("idle");
      setSessionLimitBlocked(false);
      setTokenUsageInfo(null);
      setUsageCycleInfo(null);

      setSessionId(normalizedNewSessionId);
      dispatch(setReduxSessionId(normalizedNewSessionId));
      dispatch(setRefreshSidebar(true));
      navigate("/user/new", {
        state: { sessionId: normalizedNewSessionId },
        replace: true,
      });
    } catch (err) {
      console.error("Failed to create session:", err);
      showErrorToast("Failed to create session");
    } finally {
      setIsCreatingSession(false);
    }
  }, [
    dispatch,
    isKnowledgeSearchActive,
    isCreatingSession,
    navigate,
    normalizeSessionId,
    selectedGroups,
    selectedFiles,
    selectedKnowledgeContexts,
    selectedMode,
    selectedModel,
    setUploadedDocNames,
    token,
  ]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState("Personal");
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [folderItems, setFolderItems] = useState([]);
  // const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState({
    id: null,
    name: "Root",
  });
  console.log("selectedFolder =", selectedFolder);

  const [uploadedInfo, setUploadedInfo] = useState({
    count: 0,
    type: "file",
  });

  const [attachedFiles, setAttachedFiles] = useState([]);
  // const [uploadedDocumentIds, setUploadedDocumentIds] = useState({});
  // const [uploadedDocNames, setUploadedDocNames] = useState([]);
  const fileInputRef = useRef(null);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [isDragging, setIsDragging] = useState(false);

  const resizeInputTextarea = useCallback((forceReset = false) => {
    const el = inputTextareaRef.current;
    if (!el) return;

    try {
      if (textareaResizeRafRef.current) {
        cancelAnimationFrame(textareaResizeRafRef.current);
      }
    } catch {}

    textareaResizeRafRef.current = requestAnimationFrame(() => {
      const node = inputTextareaRef.current;
      if (!node) return;

      if (forceReset) {
        node.style.height = "40px";
        node.style.overflowY = "hidden";
        return;
      }

      // Batch DOM reads/writes in one frame to reduce typing jank.
      node.style.height = "auto";
      const scrollHeight = node.scrollHeight || 0;
      const maxHeight = 200;
      const nextHeight = Math.min(scrollHeight, maxHeight);
      node.style.height = `${nextHeight}px`;
      node.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
    });
  }, []);

  useEffect(() => {
    return () => {
      try {
        if (textareaResizeRafRef.current) {
          cancelAnimationFrame(textareaResizeRafRef.current);
        }
      } catch {}
    };
  }, []);

  const hasEmailExtractFiles = useMemo(() => {
    const getExt = (name) => name?.split(".").pop()?.toLowerCase();
    return attachedFiles.some((f) => {
      const ext = getExt(f?.file?.name);
      return ext === "zip" || ext === "msg";
    });
  }, [attachedFiles]);

  useEffect(() => {
    if (!hasEmailExtractFiles) return;
    if (!input) return;
    setInput("");
    resizeInputTextarea(true);
  }, [hasEmailExtractFiles, input, resizeInputTextarea]);

  const storedUser =
    JSON.parse(localStorage.getItem("user")) ||
    JSON.parse(sessionStorage.getItem("user"));

  const profilePicPath =
    storedUser?.user_profile_pic || user?.user_profile_pic || "";

  const loggedInPic = profilePicPath
    ? `${process.env.REACT_APP_SERVER_URL}/${String(profilePicPath).replace(
        /\\/g,
        "/",
      )}`
    : `${process.env.REACT_APP_SERVER_URL}/uploads/default_img.png`;

  const stripMarkdown = (text = "") => {
    return (
      text
        // headers ### ##
        .replace(/^#{1,6}\s+/gm, "")
        // bold / italic *** ** *
        .replace(/\*\*\*([^*]+)\*\*\*/g, "$1")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\*([^*]+)\*/g, "$1")
        // inline code `
        .replace(/`([^`]+)`/g, "$1")
        // code blocks ```
        .replace(/```[\s\S]*?```/g, "")
        // links [text](url)
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        // extra newlines
        .replace(/\n{2,}/g, "\n")
        .trim()
    );
  };

  useEffect(() => {
    if (!sessionId) {
      setSessionLimitBlocked(false);
      setTokenUsageInfo(null);
      setUsageCycleInfo(null);
      setIsSessionStatusChecking(false);
      return;
    }
    // setUploadedDocNames([]);

    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  }, [sessionId]);

  const handleTextToSpeech = useCallback((text) => {
    if (!text) return;

    // 🔹 Convert markdown to plain text for TTS
    const cleanText = stripMarkdown(text);

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();

      if (
        currentUtteranceRef.current &&
        currentUtteranceRef.current.text === cleanText
      ) {
        currentUtteranceRef.current = null;
        return;
      }
    }

    const utterance = new SpeechSynthesisUtterance(cleanText);
    currentUtteranceRef.current = utterance;

    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onend = () => {
      currentUtteranceRef.current = null;
    };

    window.speechSynthesis.speak(utterance);
  }, []);

  const getBotGeneratedFileMeta = useCallback((botData = {}) => {
    const fromPayload = botData?.bot_generated_file;
    const savedGenerated =
      Array.isArray(botData?.saved_generated_files) &&
      botData.saved_generated_files.length > 0
        ? botData.saved_generated_files[0]
        : null;
    const normalizedSavedSize =
      Number.isFinite(Number(savedGenerated?.file_size)) &&
      Number(savedGenerated.file_size) > 0
        ? Number(savedGenerated.file_size)
        : null;

    const serverBase = String(process.env.REACT_APP_SERVER_URL || "").replace(
      /\/+$/,
      "",
    );

    if (fromPayload?.url) {
      // Ensure DB refs are preserved/attached even if backend only returned URL
      const merged = {
        ...fromPayload,
        user_files_id:
          fromPayload.user_files_id ?? savedGenerated?.user_files_id ?? null,
        user_files_name:
          fromPayload.user_files_name ??
          savedGenerated?.user_files_name ??
          null,
        user_files_path:
          fromPayload.user_files_path ??
          savedGenerated?.user_files_path ??
          null,
        size:
          Number.isFinite(Number(fromPayload.size)) &&
          Number(fromPayload.size) > 0
            ? Number(fromPayload.size)
            : Number.isFinite(Number(fromPayload.file_size)) &&
                Number(fromPayload.file_size) > 0
              ? Number(fromPayload.file_size)
              : normalizedSavedSize,
      };

      // Prefer backend-served URL when we have a persisted DB path
      if (merged.user_files_path && serverBase) {
        merged.url = `${serverBase}/${String(merged.user_files_path).replace(
          /\\/g,
          "/",
        )}`;
      }

      return merged;
    }

    const apiPayload =
      botData?.fastapi_response && typeof botData.fastapi_response === "object"
        ? botData.fastapi_response
        : botData;

    const fileObj =
      apiPayload?.file && typeof apiPayload.file === "object"
        ? apiPayload.file
        : null;

    const fileUrl = fileObj?.url || apiPayload?.file_path;
    if (!fileUrl || typeof fileUrl !== "string") return null;

    const explicitName = fileObj?.filename || null;
    const explicitSize =
      Number.isFinite(Number(fileObj?.file_size)) &&
      Number(fileObj.file_size) > 0
        ? Number(fileObj.file_size)
        : null;
    const explicitType = fileObj?.file_extension
      ? String(fileObj.file_extension).toLowerCase()
      : null;

    try {
      const parsed = new URL(fileUrl);
      const rawName = decodeURIComponent(
        parsed.pathname.split("/").pop() || "generated_file",
      );
      const cleanName = rawName.replace(/\s+/g, " ").trim() || "generated_file";
      const inferredType = cleanName.includes(".")
        ? cleanName.split(".").pop().toLowerCase()
        : null;

      return {
        url: fileUrl,
        name: explicitName || cleanName,
        size: explicitSize,
        type: explicitType || inferredType,
        description: apiPayload?.ppt_description || null,
      };
    } catch {
      return {
        url: fileUrl,
        name: explicitName || "generated_file",
        size: explicitSize,
        type: explicitType,
        description: apiPayload?.ppt_description || null,
      };
    }
  }, []);

  const updateResponseRecordTime = useCallback(
    async (chatId, seconds) => {
      try {
        await api.post(
          "/chat/update-response-time",
          {
            chat_id: chatId,
            response_record_time: seconds,
          },
          { skipLoader: true, silent: true },
        );
      } catch (err) {
        console.error("Failed to update response time", err);
      }
    },
    [api],
  );

  // ============================================================================
  // OPTIMIZED TYPING ANIMATION - Uses functional updates
  // ============================================================================

  const startTypingAnimation = useCallback(
    (fullResponse, botTypingId, botData) => {
      // Profile sync (automatic): session_profile replaces, user_profile merges
      // NOTE: For streaming, this is handled on the SSE "done" event.
      syncProfilesFromResponse(botData);

      activeResponseRef.current = {
        botTypingId,
        chatId: botData?.chat_id || botData?._id || null,
        fullResponse: String(fullResponse || ""),
        charIndex: 0,
      };

      const generatedFileMeta = getBotGeneratedFileMeta(botData);

      // Show generated attachment immediately, before typed response text.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botTypingId
            ? {
                ...msg,
                bot_generated_file: generatedFileMeta,
              }
            : msg,
        ),
      );

      const getTypingConfig = (len) => {
        if (len > 2000) return { delay: 16, chunk: 8 };
        if (len > 1000) return { delay: 20, chunk: 6 };
        if (len > 500) return { delay: 25, chunk: 4 };
        return { delay: 30, chunk: 2 };
      };

      isTypingRef.current = true;
      setBotStatus("requesting");

      let charIndex = 0;

      // ✅ renamed for clarity (first token logic)
      let firstTokenCaptured = false;

      const { delay, chunk } = getTypingConfig(fullResponse.length);

      typingIntervalRef.current = setInterval(() => {
        charIndex = Math.min(charIndex + chunk, fullResponse.length);
        activeResponseRef.current.charIndex = charIndex;

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== botTypingId) return msg;

            let updatedMsg = {
              ...msg,
              partialMessage: fullResponse.slice(0, charIndex),
              showDots: false,
            };

            // 🧠 STOP TIMER ON FIRST TOKEN (ONCE ONLY)
            if (!firstTokenCaptured && charIndex > 0 && msg.startedAt) {
              firstTokenCaptured = true;

              const totalSeconds = (
                (Date.now() - msg.startedAt) /
                1000
              ).toFixed(2);

              // ✅ freeze thinking time
              updatedMsg.response_record_time = totalSeconds;

              // 👉 optional backend sync
              updateResponseRecordTime(botData.chat_id, totalSeconds);
            }

            return updatedMsg;
          }),
        );

        // 🔚 FINAL RESPONSE (typing complete)
        if (charIndex >= fullResponse.length) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          activeResponseRef.current = {
            botTypingId: null,
            chatId: null,
            sessionId: null,
            fullResponse: "",
            charIndex: 0,
          };

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message: fullResponse,
                    partialMessage: "",
                    showDots: false,
                    citations: botData.citations || [],
                    rag_sources: botData.rag_sources || [],
                    follow_up: botData.follow_up || [],
                    model_name: botData.model_name,
                    model_generic_name: botData.model_generic_name,
                    model_type: normalizeModelType(
                      botData.model_type ?? botData.session_type,
                      privacy_mode ? "offline" : "online",
                    ),
                    feedback: botData.feedback ?? msg.feedback ?? null,
                    feedback_comment:
                      botData.feedback_comment ?? msg.feedback_comment ?? null,
                    response_record_time: msg.response_record_time ?? null, // preserve recorded time
                    // 🔹 METRICS (unchanged)
                    response_time: botData.response_time ?? null,
                    // response_record_time: botData.response_record_time ?? null,
                    input_tokens: botData.input_tokens ?? null,
                    output_tokens: botData.output_tokens ?? null,
                    estimated_cost_usd: botData.estimated_cost_usd ?? null,
                    confidence: botData.confidence ?? null,
                    bot_generated_file: generatedFileMeta,
                  }
                : msg,
            ),
          );

          isTypingRef.current = false;
          setIsBotResponding(false);
          setBotStatus("idle");
        }
      }, delay);
    },
    [privacy_mode, syncProfilesFromResponse],
  );

  const recoverBotResponseByChatId = useCallback(
    async ({
      chatId,
      targetSessionId,
      userText,
      botTypingId,
      startedAt,
      maxMs = 600000,
    }) => {
      if (!chatId) {
        if (targetSessionId) {
          recoverBotResponse({
            targetSessionId,
            userText,
            botTypingId,
            startedAt,
            maxMs,
          });
        }
        return;
      }

      const attemptId = ++recoveryAttemptRef.current;
      const start = Date.now();

      setBotStatus("requesting");
      setIsBotResponding(true);

      let delayMs = 1200;

      const pollOnce = async () => {
        if (recoveryAttemptRef.current !== attemptId) return;

        try {
          const res = await api.get(`/chat/result/${chatId}`);

          if (recoveryAttemptRef.current !== attemptId) return;

          const data = res?.data || {};
          const status = String(data.response_status || "").toLowerCase();

          const tokenUsage = extractTokenUsage(data);
          if (tokenUsage) {
            setTokenUsageInfoSafely(tokenUsage || data?.token_usage || null);
          }
          const usageCycle = extractUsageCycle(data);
          if (usageCycle) {
            setUsageCycleInfoSafely(usageCycle);
          }

          if (
            isSessionLimitError(data) ||
            isSessionLimitError(data?.response) ||
            isSessionLimitError(data?.message)
          ) {
            clearRecovery();
            setSessionLimitBlocked(true);
            setIsBotResponding(false);
            setBotStatus("idle");
            setMessages((prev) => prev.filter((msg) => msg.id !== botTypingId));
            handleSessionLimitReached(
              getSessionLimitMessage(data) ||
                data?.response ||
                data?.message ||
                "Session limit reached.",
            );
            return;
          }

          if (status === "success") {
            clearRecovery();
            dispatch(setRefreshSidebar(true));
            startTypingAnimation(
              data.response ||
                data.message ||
                "Apologies, there was a temporary issue processing your request.",
              botTypingId,
              data,
            );
            return;
          }

          if (status && status !== "in progress") {
            clearRecovery();
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === botTypingId
                  ? {
                      ...msg,
                      message:
                        data.response ||
                        data.message ||
                        "We apologize for the inconvenience. We’re temporarily unable to process your request due to a service issue on our end. Please try again soon.",
                      partialMessage: "",
                      showDots: false,
                      response_record_time:
                        msg.response_record_time ??
                        (
                          (Date.now() -
                            (startedAt || msg.startedAt || Date.now())) /
                          1000
                        ).toFixed(2),
                    }
                  : msg,
              ),
            );
            setIsBotResponding(false);
            setBotStatus("idle");
            dispatch(setRefreshSidebar(true));
            return;
          }
        } catch (err) {
          if (recoveryAttemptRef.current !== attemptId) return;
          const status = err?.response?.status;
          if (status === 401) {
            clearRecovery();
            setModalMessage("Your session has expired. Please login again.");
            setShowModal(true);
            setIsBotResponding(false);
            setBotStatus("idle");
            return;
          }
          // If the job API isn't available / fails, fall back to session-history recovery.
          if (targetSessionId) {
            recoverBotResponse({
              targetSessionId,
              userText,
              botTypingId,
              startedAt,
              maxMs,
            });
            return;
          }
        }

        const elapsed = Date.now() - start;
        if (elapsed >= maxMs) {
          clearRecovery();
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message:
                      "This is taking longer than usual. The server may still be generating the response—please wait a bit more or retry.",
                    partialMessage: "",
                    showDots: false,
                    response_record_time:
                      msg.response_record_time ??
                      (
                        (Date.now() -
                          (startedAt || msg.startedAt || Date.now())) /
                        1000
                      ).toFixed(2),
                  }
                : msg,
            ),
          );
          setIsBotResponding(false);
          setBotStatus("idle");
          dispatch(setRefreshSidebar(true));
          return;
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botTypingId
              ? {
                  ...msg,
                  message:
                    msg.message ||
                    "Connection issue detected. Trying to fetch the response…",
                  partialMessage: "",
                  showDots: true,
                }
              : msg,
          ),
        );

        delayMs = Math.min(Math.floor(delayMs * 1.35), 10000);
        recoveryTimerRef.current = setTimeout(pollOnce, delayMs);
      };

      pollOnce();
    },
    [clearRecovery, dispatch, recoverBotResponse, startTypingAnimation, token],
  );

  const updateBotMessage = useCallback((id, updates) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg)),
    );
  }, []);

  const handleMessageFeedback = useCallback(
    async ({ msg, feedback, comment }) => {
      if (!msg || msg.sender !== "bot") return;

      const session_id = msg.session_id || sessionId || null;
      const message_id = msg.chat_id || null;
      const responseText = String(msg.message || "").trim();
      const model =
        msg.model_name ||
        msg.model_generic_name ||
        selectedModelRef.current ||
        "";

      if (!session_id || !message_id) {
        showErrorToast(
          "Unable to submit feedback (missing session/message id).",
        );
        return;
      }

      const previous = {
        feedback: msg.feedback || null,
        feedback_comment: msg.feedback_comment || null,
      };

      updateBotMessage(msg.id, {
        feedback,
        feedback_comment: feedback === "dislike" ? comment || null : null,
        feedbackSubmitting: true,
      });

      try {
        // Toggle-off (clear) should work even if message text/model are missing.
        if (feedback != null) {
          if (!responseText) {
            showErrorToast(
              "Unable to submit feedback (missing response text).",
            );
            updateBotMessage(msg.id, {
              feedback: previous.feedback,
              feedback_comment: previous.feedback_comment,
              feedbackSubmitting: false,
            });
            return;
          }
          if (!model) {
            showErrorToast("Unable to submit feedback (missing model).");
            updateBotMessage(msg.id, {
              feedback: previous.feedback,
              feedback_comment: previous.feedback_comment,
              feedbackSubmitting: false,
            });
            return;
          }
        }

        const result = await submitFeedback({
          session_id,
          message_id,
          feedback,
          comment: feedback === "dislike" ? comment || null : null,
          response: responseText,
          model,
        });

        if (result?.skipped) {
          // Debounced/in-flight: keep UI state but don't spam backend.
          return;
        }
      } catch (err) {
        updateBotMessage(msg.id, {
          feedback: previous.feedback,
          feedback_comment: previous.feedback_comment,
        });
        showErrorToast("Failed to save feedback. Please try again.");
      } finally {
        updateBotMessage(msg.id, { feedbackSubmitting: false });
      }
    },
    [sessionId, submitFeedback, updateBotMessage],
  );

  // ============================================================================
  // DEBOUNCED SCROLL HANDLER
  // ============================================================================

  const debouncedScrollCheck = useCallback(
    debounce(() => {
      const chatBox = document.querySelector(".chat-box");
      if (!chatBox) return;

      const atBottom =
        chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;
      setShowScrollDown(!atBottom);
    }, 100),
    [],
  );

  // ============================================================================
  // FILTERED MESSAGES - Memoized to prevent recalculation
  // ============================================================================

  const filteredMessages = useMemo(() => {
    const term = String(searchTerm || "")
      .toLowerCase()
      .trim();
    if (!term) return messages;

    return messages.filter((msg) => {
      // Always keep the live "thinking/typing" bubble visible.
      if (msg?.sender === "bot" && msg?.showDots) return true;

      const text =
        (typeof msg?.message === "string" ? msg.message : "") +
        " " +
        (typeof msg?.partialMessage === "string" ? msg.partialMessage : "");

      return text.toLowerCase().includes(term);
    });
  }, [messages, searchTerm]);

  // ============================================================================
  // RECORDING HANDLERS
  // ============================================================================

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      setIsRecording(true);
      setInterimTranscript("");
      setTempTranscript("");

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
    } catch (err) {
      console.error("Mic error:", err);
      showErrorToast("Microphone access denied");
    }
  };

  const stopRecording = async () => {
    setIsRecording(false);

    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    recorder.stop();

    recorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });

      const formData = new FormData();
      formData.append("file", audioBlob, "audio.webm");

      try {
        // 🔄 START PROCESSING
        setIsProcessingSTT(true);
        setInterimTranscript("Processing...");

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/chat/realtime-stt`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error("STT request failed");
        }

        const data = await response.json();
        const text = data?.transcription?.trim();

        if (text) {
          setInput((prev) => prev + text + " ");
        }
      } catch (err) {
        console.error("STT error:", err);
        showErrorToast("Speech recognition failed");
      } finally {
        // ✅ STOP PROCESSING
        setIsProcessingSTT(false);
        setMicOpen(false);
        setInterimTranscript("");
        setTempTranscript("");
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
      }
    };
  };

  const cancelRecording = useCallback(() => {
    setIsRecording(false);

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    audioChunksRef.current = [];
    setMicOpen(false);
    setInterimTranscript("");
    setTempTranscript("");
  }, []);

  // Keep other actions available even if a session is blocked; we only disable sending.

  const toggleReadAloud = useCallback(() => {
    if (!input) return;
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      const utterance = new SpeechSynthesisUtterance(input);
      utterance.lang = "en-US";
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  }, [input]);

  // ============================================================================
  // FILE HANDLERS
  // ============================================================================

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const MAX_ATTACHABLE_FILES = 20;

  // Allowed extensions
  const allowedExtensions = [
    "pdf",
    "docx",
    "txt",
    "doc",
    "pptx",
    "xlsx",
    "zip",
    "msg",
    "mp3",
    "wav",
    "aac",
    "flac",
    "mp4",
    "mov",
    "avi",
    "mkv",
    "flv",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "bmp",
    "tiff",
  ];

  /**
   * Get per-file-type size limit in bytes.
   */
  const getSizeLimit = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();

    // ZIP/RAR archives: 400 MB
    if (ext === "zip" || ext === "rar") {
      return 400 * 1024 * 1024;
    }

    // PPT/PPTX and DOC/DOCX: 100 MB
    if (
      ext === "ppt" ||
      ext === "pptx" ||
      ext === "doc" ||
      ext === "docx"
    ) {
      return 100 * 1024 * 1024;
    }

    // All other files: 50 MB
    return 50 * 1024 * 1024;
  };

  /**
   * Get human-readable size limit string for error messages.
   */
  const getSizeLimitLabel = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();

    if (ext === "zip" || ext === "rar") return "400 MB";
    if (
      ext === "ppt" ||
      ext === "pptx" ||
      ext === "doc" ||
      ext === "docx"
    )
      return "100 MB";

    return "50 MB";
  };

  const filterSupportedFiles = (files) => {
    const supported = [];
    const unsupported = [];
    const oversized = [];

    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();

      // Check extension support
      if (!allowedExtensions.includes(ext)) {
        unsupported.push(file);
        return;
      }

      // Check size limit per file type
      const sizeLimit = getSizeLimit(file.name);
      if (file.size > sizeLimit) {
        oversized.push({
          name: file.name,
          size: file.size,
          limitLabel: getSizeLimitLabel(file.name),
        });
        return;
      }

      supported.push(file);
    });

    return { supported, unsupported, oversized };
  };

  // Handle file selection new
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const { supported, unsupported, oversized } = filterSupportedFiles(files);

    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
    }

    if (oversized.length > 0) {
      oversized.forEach((f) => {
        const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
        showErrorToast(
          `"${f.name}" (${sizeMB} MB) exceeds the ${f.limitLabel} size limit.`,
        );
      });
    }

    if (supported.length === 0) return;

    const currentFileCount = attachedFiles.length;
    if (currentFileCount >= MAX_ATTACHABLE_FILES) {
      showErrorToast(
        `You can upload a maximum of ${MAX_ATTACHABLE_FILES} files at a time.`,
      );
      e.target.value = "";
      return;
    }

    let limitedFiles = supported;
    if (currentFileCount + supported.length > MAX_ATTACHABLE_FILES) {
      const remaining = MAX_ATTACHABLE_FILES - currentFileCount;
      limitedFiles = supported.slice(0, remaining);
      showInfoToast(
        `Added ${remaining} file(s) (limit ${MAX_ATTACHABLE_FILES} files).`,
      );
    }

    // 1️⃣ Validate filenames (DO NOT HARD BLOCK)
    const validNameFiles = [];
    const invalidNameFiles = [];
    const normalize = (s) => s?.trim().toLowerCase();

    const extractDuplicateNames = (duplicates = []) =>
      duplicates.map((d) => normalize(d.originalname || d.filename));

    for (const file of limitedFiles) {
      const { valid, reason } = validateFileName(file.name);

      if (valid) {
        validNameFiles.push(file);
      } else {
        invalidNameFiles.push({ name: file.name, reason });
      }
    }

    if (invalidNameFiles.length > 0) {
      const count = invalidNameFiles.length;

      // 🧠 Adaptive timing
      const autoCloseTime = count <= 2 ? 2000 : count <= 4 ? 1600 : 1200;

      const gap = autoCloseTime + 150;

      invalidNameFiles.forEach((f, index) => {
        setTimeout(() => {
          showErrorToast(`${f.reason}${truncate(f.name)}`, {
            autoClose: autoCloseTime,
          });
        }, index * gap);
      });
    }

    if (validNameFiles.length === 0) {
      e.target.value = "";
      return;
    }

    // 2️⃣ Optimistically add files as "uploading"
    const getExt = (name) => name?.split(".").pop()?.toLowerCase();
    const isEmailExtractFile = (file) => {
      const ext = getExt(file?.name);
      // .msg files are email extract files (attached directly to chat)
      // ZIP files now flow through the normal upload pipeline where
      // the backend inspects them: email-archives (.msg inside) stay
      // as-is while document archives are extracted & processed per-file.
      return ext === "msg";
    };

    // Split: email-extract files (.zip/.msg) should NOT be uploaded to library
    const emailFiles = validNameFiles.filter(isEmailExtractFile);
    const libraryFiles = validNameFiles.filter((f) => !isEmailExtractFile(f));

    // Enforce: only one zip total (existing + newly selected)
    const existingZipCount = attachedFiles.filter(
      (f) => getExt(f?.file?.name) === "zip",
    ).length;
    const newZipFiles = emailFiles.filter((f) => getExt(f?.name) === "zip");

    let emailFilesAllowed = emailFiles;
    if (existingZipCount > 0 && newZipFiles.length > 0) {
      showErrorToast("Only one .zip file is allowed.");
      emailFilesAllowed = emailFilesAllowed.filter(
        (f) => getExt(f?.name) !== "zip",
      );
    } else if (newZipFiles.length > 1) {
      showErrorToast("Only one .zip file is allowed.");
      const [firstZip] = newZipFiles;
      emailFilesAllowed = [
        ...emailFilesAllowed.filter((f) => getExt(f?.name) !== "zip"),
        firstZip,
      ];
    }

    const emailTempFiles = emailFilesAllowed.map((file) => ({
      file,
      status: "done",
      clientId: `${file.name}-${file.size}-${file.lastModified}`,
    }));

    const tempFiles = libraryFiles.map((file) => ({
      file,
      status: "pending", // ✅ Don't show spinner yet
      clientId: `${file.name}-${file.size}-${file.lastModified}`,
    }));

    setAttachedFiles((prev) => [...prev, ...emailTempFiles, ...tempFiles]);

    if (emailTempFiles.length > 0) {
      setInput("");
      resizeInputTextarea(true);
    }

    if (libraryFiles.length === 0) {
      e.target.value = "";
      return;
    }

    const selectedNames = libraryFiles.map((f) => normalize(f.name));

    setUploadedDocNames((prev) => [...new Set([...prev, ...selectedNames])]);
    // Store files temporarily. Do NOT upload yet.
    setPendingUploadFiles(libraryFiles);

    setUploadedInfo({
      count: libraryFiles.length,
      type: libraryFiles.length > 1 ? "files" : "file",
    });

    setShowUploadModal(true);

    e.target.value = "";
    return;
  };

  const handleUploadFiles = async () => {
    console.log("handleUploadFiles called");
    console.log("Selected Folder:", selectedFolder);
    // if (!selectedFolder) {
    //   showErrorToast("Please select a folder.");
    //   return;
    // }

    setAttachedFiles((prev) =>
      prev.map((item) => ({
        ...item,
        status: item.status === "pending" ? "uploading" : item.status,
      })),
    );
    // ✅ Close the upload modal after successful upload
    setShowUploadModal(false);
    setSelectedFolder({
      id: null,
      name: "Root",
    });

    const formData = new FormData();

    pendingUploadFiles.forEach((file) => formData.append("files", file));

    formData.append("model", selectedModel);
    formData.append("mode", uploadMode.toLowerCase());
    formData.append("folder_id", selectedFolder.id);

    try {
      const token =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");

      const uploadEndpoint = `${process.env.REACT_APP_API_URL}/uploaded-files/newUploadv2`;

      // 4️⃣ Upload files
      const res = await api.post(uploadEndpoint, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ NEW BACKEND CONTRACT
      const { toasts = [] } = res.data;

      // Capture document_id mappings (best-effort; backward compatible)
      try {
        const nextIds = {};

        const normalize = (s) => s?.trim().toLowerCase();

        const filesSuccess =
          res.data?.fastapi_response?.files_success ||
          res.data?.fastapi_response?.files ||
          [];
        if (Array.isArray(filesSuccess)) {
          filesSuccess.forEach((row) => {
            const key = normalize(
              row?.file || row?.filename || row?.original_filename,
            );
            const docId = row?.document_id || row?.documentId;
            if (key && docId) nextIds[key] = String(docId);
          });
        }

        const processedFiles = res.data?.files || [];
        if (Array.isArray(processedFiles)) {
          processedFiles.forEach((row) => {
            const key = normalize(row?.file?.originalname);
            const docId = row?.originalMeta?.document_id;
            if (key && docId) nextIds[key] = String(docId);
          });
        }

        if (Object.keys(nextIds).length > 0) {
          setUploadedDocumentIds((prev) => ({ ...(prev || {}), ...nextIds }));
        }
      } catch {}
      // console.log("🔥 Toasts received:", toasts.length, toasts);

      if (toasts.length > 0) {
        const count = toasts.length;

        // 🧠 Adaptive timing (same philosophy as filename validation)
        const autoCloseTime = count <= 2 ? 2200 : count <= 4 ? 1800 : 1400;

        const gap = autoCloseTime + 150;

        toasts.forEach((toast, index) => {
          setTimeout(() => {
            const options = {
              autoClose: autoCloseTime,
              toastId: `upload-${index}-${toast.message}`,
            };

            const message = formatToastMessage(toast.message);

            if (toast.type === "success") {
              showSuccessToast(message, options);
            } else if (toast.type === "warning") {
              (showSuccessToast || showErrorToast)(message, options);
            } else {
              showSuccessToast(message, options);
            }
          }, index * gap);
        });
      }
      const normalize = (s) => s?.trim().toLowerCase();

      /* -----------------------------------------------
         🔍 Extract filenames from toasts BEFORE updating
         attachedFiles, so ZIP archives can be replaced
         with individual extracted file entries.
      ----------------------------------------------- */
      const archiveExtPattern = /\.(zip|rar)$/i;
      const extractedFilenames = toasts
        .map((t) => {
          const msg = t.message || "";
          const match = msg.match(
            /^(.+?)\s+(uploaded successfully|already exists|is pending|exists in)/i,
          );
          return match ? match[1].trim() : null;
        })
        .filter(Boolean);

      // 2️⃣ Update UI statuses — replace ZIP entries with extracted files
      setAttachedFiles((prev) =>
        prev.flatMap((f) => {
          const ext = f?.file?.name?.split(".").pop()?.toLowerCase();
          const isArchive = ext === "zip" || ext === "rar";

          const fileName = normalize(f.file.name);

          const success = toasts.find(
            (t) =>
              t.type === "success" &&
              t.message.toLowerCase().includes(f.file.name.toLowerCase()),
          );

          const duplicate = toasts.find(
            (t) =>
              t.message.toLowerCase().includes(f.file.name.toLowerCase()) &&
              (t.message.includes("already exists") ||
                t.message.includes("pending")),
          );

          const failed = toasts.find(
            (t) =>
              t.type === "error" &&
              t.message.toLowerCase().includes(f.file.name.toLowerCase()),
          );

          // ZIP/RAR archive: replace the single archive entry with
          // individual synthetic entries for each extracted file,
          // so attached_document, file_names, and document_ids all
          // reference the extracted documents.
          if (isArchive && extractedFilenames.length > 0) {
            const hasAnyError = toasts.some((t) => t.type === "error");
            if (hasAnyError) {
              return [{ ...f, status: "error" }];
            }
            return extractedFilenames.map((name, idx) => ({
              file: new File([], name),
              status: "done",
              clientId: `${f.clientId}-extracted-${idx}`,
            }));
          }

          // Archive with no extracted filenames (fallback)
          if (isArchive) {
            const hasAnyError = toasts.some((t) => t.type === "error");
            return [{ ...f, status: hasAnyError ? "error" : "done" }];
          }

          if (success) return [{ ...f, status: "done" }];
          if (duplicate) return [{ ...f, status: "already_trained" }];
          if (failed) return [{ ...f, status: "error" }];

          return [f];
        }),
      );

      // 🔄 Update uploadedDocNames to replace archive names with extracted filenames
      if (extractedFilenames.length > 0) {
        setUploadedDocNames((prev) => {
          // Remove any archive names that were added at file-selection time
          const withoutArchives = prev.filter(
            (name) => !archiveExtPattern.test(name),
          );
          // Add the actual extracted filenames (lowercased, Set deduplicates)
          const normalizedExtracted = extractedFilenames.map((n) =>
            n.toLowerCase(),
          );
          return [...new Set([...withoutArchives, ...normalizedExtracted])];
        });
      }
    } catch (error) {
      const status = error.response?.status;
      const backendMessage =
        error.response?.data?.message || error.response?.data?.error;

      console.error("Upload failed:", {
        status,
        backendMessage,
        error,
      });

      // 🟡 ONLY mark already_trained if backend explicitly says duplicate
      const isDuplicate =
        backendMessage &&
        backendMessage.toLowerCase().includes("already exists");

      // 🔴 BLOCKED BY BACKEND (Global library conflict)
      if (isDuplicate) {
        showSuccessToast(backendMessage);

        // 🚨 STOP SPINNER — mark uploading files as error
        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.status === "uploading" ? { ...f, status: "already_trained" } : f,
          ),
        );
      } else {
        // 🔴 REAL UNKNOWN ERROR
        console.error("Upload failed:", error);

        showErrorToast(
          backendMessage || "Upload failed. Please try again later.",
        );

        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.status === "uploading" ? { ...f, status: "error" } : f,
          ),
        );
      }
    }
  };

  useEffect(() => {
    if (showUploadModal) {
      fetchFoldersForMode(uploadMode);
    }
  }, [showUploadModal, uploadMode]);

  const buildTree = (items) => {
    const map = {};
    const roots = [];

    items.forEach((item) => {
      map[item.id] = {
        ...item,
        children: [],
      };
    });

    items.forEach((item) => {
      if (item.parentId && map[item.parentId]) {
        map[item.parentId].children.push(map[item.id]);
      } else {
        roots.push(map[item.id]);
      }
    });

    return roots;
  };

  const fetchFoldersForMode = async (mode) => {
    const token = localStorage.getItem("access_token");

    const response = await api.get(
      `${process.env.REACT_APP_API_URL}/trained-documents/get-trained-docs`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const folders = response.data.filter(
      (item) => item.itemType === "folder" && item.mode === mode.toLowerCase(),
    );

    setFolderItems(buildTree(folders));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    // Reuse existing logic
    handleFileChange({ target: { files } });
  };

  const removeAttachedFile = useCallback((index) => {
    setAttachedFiles((prev) => {
      const fileToRemove = prev[index]?.file;
      const removedName = fileToRemove?.name?.trim().toLowerCase();

      // update uploadedDocNames safely
      if (removedName) {
        setUploadedDocNames((docs) =>
          docs.filter((name) => name !== removedName),
        );
        setUploadedDocumentIds((prevIds) => {
          const next = { ...(prevIds || {}) };
          delete next[removedName];
          return next;
        });
      }

      // remove file from UI list
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleCancelq = useCallback(() => {
    setErrorModal({ show: false, message: "" });
    setAttachedFiles([]);
  }, []);

  const handleOk = useCallback(() => {
    setErrorModal({ show: false, message: "" });
    navigate("/user/chat/document");
  }, [navigate]);

  const formatToastMessage = (message) => {
    if (!message) return message;

    // Try to detect filenames with extensions
    const fileRegex = /([\w,\s-]+\.[A-Za-z]{2,6})/g;

    return message.replace(fileRegex, (match) => truncate(match));
  };

  // ============================================================================
  // MAIN SEND HANDLER
  // ============================================================================

  const streamUnsubsRef = useRef([]);
  const streamResumeAttemptedRef = useRef(new Set());
  const modelSuggestionToastShownRef = useRef(new Set());

  const maybeShowModelSuggestionToast = useCallback(
    (
      payload,
      { chatId = null, sessionId = null, clientMessageId = null } = {},
    ) => {
      const root = payload && typeof payload === "object" ? payload : null;
      if (!root) return;

      const suggestion =
        root.model_suggestion ||
        root?.result?.model_suggestion ||
        root?.fastapi_response?.model_suggestion ||
        root?.result?.fastapi_response?.model_suggestion ||
        null;

      if (!suggestion || typeof suggestion !== "object") return;

      const show =
        suggestion.show === true ||
        suggestion.show === 1 ||
        suggestion.show === "1" ||
        String(suggestion.show || "")
          .trim()
          .toLowerCase() === "true";
      if (!show) return;

      const message = String(suggestion.message || "").trim();
      if (!message) return;

      const stableKey =
        (chatId != null && String(chatId)) ||
        (sessionId &&
          clientMessageId &&
          `${String(sessionId)}:${String(clientMessageId)}`) ||
        `${message}:${String(suggestion.recommended_model || "")}`;

      if (modelSuggestionToastShownRef.current.has(stableKey)) return;
      modelSuggestionToastShownRef.current.add(stableKey);

      // Disabled per request: model suggestion info toast (e.g. "Switch to Claude...").
      // showInfoToast(message, { toastId: `model-suggestion:${stableKey}`, autoClose: 5000 });
    },
    [],
  );

  const clearStreamUnsubs = useCallback(() => {
    const unsubs = streamUnsubsRef.current || [];
    streamUnsubsRef.current = [];
    unsubs.forEach((fn) => {
      try {
        fn?.();
      } catch {}
    });
  }, []);

  const bindManagedStreamToUi = useCallback(
    ({ stream, botTypingId, currentSessionId }) => {
      if (!stream) return;

      let firstTokenCaptured = false;
      let streamSessionId = currentSessionId;
      let finalCleanupTimer = null;

      const cancelScheduledCleanup = () => {
        if (finalCleanupTimer) {
          clearTimeout(finalCleanupTimer);
          finalCleanupTimer = null;
        }
      };

      const scheduleFinalCleanup = (delayMs = 500) => {
        cancelScheduledCleanup();
        finalCleanupTimer = setTimeout(() => {
          clearActiveStreamState(streamSessionId);
          clearStreamUnsubs();
        }, delayMs);
      };

      const getStreamTrace = () =>
        stream.getSnapshot?.().executionTrace ||
        stream.executionTrace ||
        createEmptyExecutionTrace();

      const updateBotByChatId = (chatId, patch) => {
        if (!chatId) return;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.sender === "bot" && String(msg.chat_id) === String(chatId)
              ? { ...msg, ...patch }
              : msg,
          ),
        );
      };

      const updateBotTypingRow = (patch) => {
        if (!botTypingId) return;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botTypingId ? { ...msg, ...patch } : msg,
          ),
        );
      };

      const applyExecutionTrace = (trace, extraPatch = {}) => {
        const normalizedTrace =
          trace && typeof trace === "object" ? trace : createEmptyExecutionTrace();
        const patch = {
          executionTrace: normalizedTrace,
          streamSteps: Array.isArray(normalizedTrace?.steps)
            ? normalizedTrace.steps
            : [],
          ...extraPatch,
        };

        updateBotTypingRow(patch);

        const chatId = stream.chatId;
        if (chatId) updateBotByChatId(chatId, patch);
      };

      const applyStreamingStatus = (statusText) => {
        const message = String(statusText || "").trim();
        if (!message) return;

        updateBotTypingRow({ streamingStatus: message });

        const chatId = stream.chatId;
        if (chatId) updateBotByChatId(chatId, { streamingStatus: message });
      };

      // If stream already progressed before we subscribed, apply snapshot immediately.
      try {
        const snap = stream.getSnapshot?.();
        if (snap?.chatId && botTypingId) {
          updateBotTypingRow({
            chat_id: snap.chatId,
            response_status: "in progress",
          });
        }
        if (snap?.chatId && snap?.fullText) {
          updateBotByChatId(snap.chatId, {
            partialMessage: snap.fullText,
            showDots: false,
            response_status: "in progress",
          });
        }
        if (
          typeof snap?.activityMessage === "string" &&
          snap.activityMessage.trim()
        ) {
          applyStreamingStatus(snap.activityMessage);
        }
        if (isTraceVisible(snap?.executionTrace)) {
          applyExecutionTrace(snap.executionTrace);
        }
        if (snap?.chatId && snap?.ttftSeconds != null) {
          const ttft = Number(snap.ttftSeconds);
          const formatted = Number.isFinite(ttft) ? ttft.toFixed(2) : null;
          updateBotByChatId(snap.chatId, { response_record_time: formatted });
        }
      } catch {}

      const unsubMeta = stream.on("meta", (data) => {
        const chatId = data?.chat_id || null;
        chatStreamDebugLog("sse_meta", {
          session_id: streamSessionId,
          chat_id: chatId,
          model_name: data?.model_name || null,
        });

        isTypingRef.current = true;
        setBotStatus("requesting");
        setIsBotResponding(true);

        // Keep stop button working even before first chunk.
        activeResponseRef.current = {
          botTypingId:
            botTypingId || activeResponseRef.current?.botTypingId || null,
          chatId: chatId || activeResponseRef.current?.chatId || null,
          sessionId: streamSessionId,
          fullResponse: stream.fullText || "",
          charIndex: (stream.fullText || "").length,
        };

        if (chatId) {
          updateBotTypingRow({
            chat_id: chatId,
            model_name: data?.model_name || null,
            model_generic_name: data?.model_generic_name || null,
            model_type: normalizeModelType(
              data?.model_type || "Online",
              "online",
            ),
            response_status: "in progress",
          });

          writeActiveStreamState(streamSessionId, {
            chat_id: chatId,
            session_id: streamSessionId,
          });
        }

        if (isTraceVisible(getStreamTrace())) {
          applyExecutionTrace(getStreamTrace());
        }

        // Handle server-issued session switch (rare but supported)
        if (data?.session_id && !sameId(data.session_id, currentSessionId)) {
          skipHistoryFetchRef.current = true;
          const normalized = normalizeSessionId(data.session_id);
          setSessionId(normalized);
          streamSessionId = normalized;
          dispatch(setReduxSessionId(normalized));
          navigate("/user/new", {
            state: { sessionId: normalized },
            replace: true,
          });
        }
      });

      const unsubActivity = stream.on("activity", (data) => {
        applyStreamingStatus(data?.message);
        applyExecutionTrace(getStreamTrace());
      });

      const unsubStreamEvent = stream.on("event", ({ event, data }) => {
        const eventUsageCycle = extractUsageCycle(data);
        if (eventUsageCycle) {
          setUsageCycleInfoSafely(eventUsageCycle);
        }
        const eventTokenUsage = extractTokenUsage(data);
        if (eventTokenUsage) {
          setTokenUsageInfoSafely(eventTokenUsage);
        }
        if (
          String(event || "").startsWith("step.") ||
          String(event || "").trim() === "execution"
        ) {
          applyExecutionTrace(getStreamTrace());
        }
      });

      const unsubStep = stream.on("step", ({ executionTrace }) => {
        applyExecutionTrace(executionTrace || getStreamTrace());
      });

      const unsubChunk = stream.on("chunk", ({ fullText, token }) => {
        const chatId = stream.chatId;
        if (!chatId) return;

        const safeFullText = String(fullText || "");
        const safeToken = token == null ? "" : String(token);

        let mergedText = safeFullText;
        try {
          const current = messagesRef.current.find(
            (m) => m?.sender === "bot" && String(m.chat_id) === String(chatId),
          );
          const currentText = String(
            current?.partialMessage || current?.message || "",
          );
          // Defensive: if resumed stream is emitting only the suffix, keep what we
          // already rendered and append new tokens instead of replacing.
          if (currentText && safeFullText.length < currentText.length) {
            mergedText = currentText + safeToken;
          }
        } catch {}

        chatStreamDebugLog("sse_chunk", {
          session_id: streamSessionId,
          chat_id: chatId,
          len: mergedText.length,
        });

        activeResponseRef.current = {
          botTypingId:
            botTypingId || activeResponseRef.current?.botTypingId || null,
          chatId,
          sessionId: streamSessionId,
          fullResponse: mergedText,
          charIndex: mergedText.length,
        };

        if (!firstTokenCaptured && botTypingId) {
          firstTokenCaptured = true;
        }

        updateBotByChatId(chatId, {
          partialMessage: mergedText,
          showDots: false,
          response_status: "in progress",
        });

        writeActiveStreamState(streamSessionId, {
          chat_id: chatId,
          session_id: streamSessionId,
          lastLen: mergedText.length,
        });

        if (isTraceVisible(getStreamTrace())) {
          applyExecutionTrace(getStreamTrace());
        }
      });

      const unsubFirstToken = stream.on("first_token", ({ ttftSeconds }) => {
        const chatId = stream.chatId;
        const ttft = Number(ttftSeconds ?? 0);
        const formatted = Number.isFinite(ttft) ? ttft.toFixed(2) : null;

        // Persist TTFT to backend (best-effort)
        if (chatId && formatted) {
          updateResponseRecordTime(chatId, formatted);
        }

        if (chatId) {
          updateBotByChatId(chatId, { response_record_time: formatted });
        } else if (botTypingId) {
          updateBotTypingRow({ response_record_time: formatted });
        }
      });

      const finalizeForChatId = (
        chatId,
        { fullText, resultObj, responseTime, errorAppend, traceOverride },
      ) => {
        const botDataLike = resultObj
          ? {
              ...resultObj,
              chat_id: chatId,
              response_status: "success",
              response: resultObj.response ?? fullText,
              response_time: responseTime ?? resultObj.response_time ?? null,
              citations: Array.isArray(resultObj.citations)
                ? resultObj.citations
                : [],
              rag_sources: Array.isArray(resultObj.rag_sources)
                ? resultObj.rag_sources
                : [],
              follow_up: Array.isArray(resultObj.follow_up)
                ? resultObj.follow_up
                : resultObj.follow_up
                  ? [resultObj.follow_up]
                  : [],
              input_tokens: resultObj.input_tokens ?? null,
              output_tokens: resultObj.output_tokens ?? null,
              estimated_cost_usd: resultObj.estimated_cost_usd ?? null,
              model_name:
                stream.modelMeta?.model_name || resultObj.model || null,
              model_generic_name:
                stream.modelMeta?.model_generic_name || resultObj.model || null,
              model_type: stream.modelMeta?.model_type || "Online",
              session_id: resultObj.session_id || streamSessionId,
              fastapi_response: resultObj,
            }
          : null;

        const generatedFileMeta = botDataLike
          ? getBotGeneratedFileMeta(botDataLike)
          : null;

        const finalMainText = (fullText || "").trim();
        const combinedMessage =
          finalMainText +
          (errorAppend ? (finalMainText ? "\n\n" : "") + errorAppend : "");
        const executionTrace =
          traceOverride && typeof traceOverride === "object"
            ? traceOverride
            : getStreamTrace();

        updateBotByChatId(chatId, {
          message: combinedMessage,
          partialMessage: "",
          showDots: false,
          streamingStatus: "",
          response_status: "success",
          citations: botDataLike?.citations || [],
          rag_sources: botDataLike?.rag_sources || [],
          follow_up: botDataLike?.follow_up || [],
          model_name: botDataLike?.model_name || null,
          model_generic_name: botDataLike?.model_generic_name || null,
          model_type: normalizeModelType(
            botDataLike?.model_type || "Online",
            "online",
          ),
          response_time: botDataLike?.response_time ?? responseTime ?? null,
          input_tokens: botDataLike?.input_tokens ?? null,
          output_tokens: botDataLike?.output_tokens ?? null,
          estimated_cost_usd: botDataLike?.estimated_cost_usd ?? null,
          confidence: botDataLike?.confidence ?? null,
          bot_generated_file: generatedFileMeta,
          executionTrace,
          streamSteps: Array.isArray(executionTrace?.steps)
            ? executionTrace.steps
            : [],
        });

        activeResponseRef.current = {
          botTypingId: null,
          chatId: null,
          sessionId: null,
          fullResponse: "",
          charIndex: 0,
        };
        isTypingRef.current = false;
        setIsBotResponding(false);
        setBotStatus("idle");
        dispatch(setRefreshSidebar(true));
      };

      const unsubDone = stream.on(
        "done",
        ({ fullText, result, response_time, raw }) => {
          const chatId = stream.chatId;
          if (!chatId) return;
          chatStreamDebugLog("sse_done", {
            session_id: streamSessionId,
            chat_id: chatId,
            len: String(fullText || "").length,
            response_time: response_time ?? null,
            has_result: Boolean(result),
            has_raw: Boolean(raw),
          });

          // Profile sync (automatic): only on final/done event (not on chunks)
          syncProfilesFromResponse(result || raw);

          // Optional UX: show a single model suggestion toast (if FastAPI sends it)
          maybeShowModelSuggestionToast(result || raw, {
            chatId,
            sessionId: streamSessionId,
          });

          const payloadForLimit = result || raw || {};
          const tokenUsage = extractTokenUsage(payloadForLimit);
          if (tokenUsage) {
            setTokenUsageInfoSafely(
              tokenUsage || payloadForLimit?.token_usage || null,
            );
          }
          const usageCycle = extractUsageCycle(payloadForLimit);
          if (usageCycle) {
            setUsageCycleInfoSafely(usageCycle);
          }
          const reached =
            isSessionLimitError(payloadForLimit) ||
            isSessionLimitError(fullText) ||
            (tokenUsage && isSessionLimitReached(tokenUsage));

          let limitMessage = null;
          if (reached) {
            limitMessage = getSessionLimitMessage(payloadForLimit);
            setTokenUsageInfoSafely(
              tokenUsage || payloadForLimit?.token_usage || null,
            );
            if (usageCycle) {
              setUsageCycleInfoSafely(usageCycle);
            }
            setSessionLimitBlocked(true);
            writeStoredSessionBlocked(
              payloadForLimit?.session_id || streamSessionId,
              true,
            );
            isTypingRef.current = false;
            setIsBotResponding(false);
            setBotStatus("idle");
            handleSessionLimitReached(limitMessage);

            // 💾 Persist the error message to the DB if we have a chatId
            // 🚫 We do NOT append limitMessage here to avoid showing it on refresh
            if (chatId) {
              const finalBotText = (fullText || "").trim();
              api
                .post(`${process.env.REACT_APP_API_URL}/chat/stop/${chatId}`, {
                  bot_response: finalBotText,
                  response_status: "fail",
                })
                .catch(() => {});
            }
          }

          finalizeForChatId(chatId, {
            fullText: fullText || "",
            resultObj: result || null,
            responseTime: response_time ?? null,
            errorAppend: null, // 🚫 We do NOT append limitMessage to the UI
            traceOverride: getStreamTrace(),
          });

          scheduleFinalCleanup();
        },
      );

      const unsubStop = stream.on("stop", () => {
        const chatId = stream.chatId;
        chatStreamDebugLog("sse_stop", {
          session_id: streamSessionId,
          chat_id: chatId,
        });
        if (chatId) {
          // Treat stop as a completed lifecycle so UI can reveal post-response actions.
          setMessages((prev) =>
            prev.map((m) =>
              m.sender === "bot" && String(m.chat_id) === String(chatId)
                ? {
                    ...m,
                    message: m.partialMessage || m.message || "",
                    partialMessage: "",
                    showDots: false,
                    streamingStatus: "",
                    response_status: "stopped",
                    executionTrace: getStreamTrace(),
                    streamSteps: getStreamTrace()?.steps || [],
                  }
                : m,
            ),
          );
        }
        isTypingRef.current = false;
        setIsBotResponding(false);
        setBotStatus("idle");
        dispatch(setRefreshSidebar(true));
        cancelScheduledCleanup();
        clearActiveStreamState(streamSessionId);
        clearStreamUnsubs();
      });

      const unsubError = stream.on("error", (data) => {
        const chatId = stream.chatId;
        chatStreamDebugLog("sse_error", {
          session_id: streamSessionId,
          chat_id: chatId,
          message: data?.message || null,
        });
        const message =
          data?.message ||
          "We apologize for the inconvenience. Please try again soon.";
        const latestTrace = getStreamTrace();

        const appendPatch = (m) => {
          const currentText = (m.message || m.partialMessage || "").trim();
          return {
            ...m,
            message: currentText + (currentText ? "\n\n" : "") + message,
            partialMessage: "",
            showDots: false,
            streamingStatus: "",
            response_status: "fail",
            executionTrace: latestTrace,
            streamSteps: latestTrace?.steps || [],
          };
        };

        const finalizePatch = (m) => {
          const currentText = (m.message || m.partialMessage || "").trim();
          return {
            ...m,
            message: currentText, // 🚫 Do NOT append the error message
            partialMessage: "",
            showDots: false,
            streamingStatus: "",
            response_status: "fail",
            executionTrace: latestTrace,
            streamSteps: latestTrace?.steps || [],
          };
        };

        if (isSessionLimitError(data) || isSessionLimitError(message)) {
          setSessionLimitBlocked(true);
          writeStoredSessionBlocked(data?.session_id || streamSessionId, true);
          isTypingRef.current = false;
          setIsBotResponding(false);
          setBotStatus("idle");
          handleSessionLimitReached(message);

          let hasContent = false;
          if (chatId) {
            const target = messagesRef.current.find(
              (m) => m.sender === "bot" && String(m.chat_id) === String(chatId),
            );
            if (
              target &&
              (target.message || target.partialMessage || "").trim()
            ) {
              hasContent = true;
            }
          } else if (botTypingId) {
            const target = messagesRef.current.find(
              (m) => m.id === botTypingId,
            );
            if (
              target &&
              (target.message || target.partialMessage || "").trim()
            ) {
              hasContent = true;
            }
          }

          if (hasContent) {
            if (chatId) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.sender === "bot" && String(m.chat_id) === String(chatId)
                    ? finalizePatch(m)
                    : m,
                ),
              );
            } else if (botTypingId) {
              setMessages((prev) =>
                prev.map((m) => (m.id === botTypingId ? finalizePatch(m) : m)),
              );
            }

            // 💾 Persist the partial text to the DB if we have a chatId
            if (chatId) {
              const target = messagesRef.current.find(
                (m) =>
                  m.sender === "bot" && String(m.chat_id) === String(chatId),
              );
              const finalBotText = target ? finalizePatch(target).message : "";
              api
                .post(`${process.env.REACT_APP_API_URL}/chat/stop/${chatId}`, {
                  bot_response: finalBotText,
                  response_status: "fail",
                })
                .catch(() => {});
            }
          } else {
            // Remove the empty bubble entirely
            if (chatId) {
              setMessages((prev) =>
                prev.filter(
                  (m) =>
                    !(
                      m.sender === "bot" && String(m.chat_id) === String(chatId)
                    ),
                ),
              );
            } else if (botTypingId) {
              setMessages((prev) => prev.filter((m) => m.id !== botTypingId));
            }

            if (chatId) {
              api
                .post(`${process.env.REACT_APP_API_URL}/chat/stop/${chatId}`, {
                  bot_response: "",
                  response_status: "fail",
                })
                .catch(() => {});
            }
          }
          cancelScheduledCleanup();
          clearActiveStreamState(streamSessionId);
          clearStreamUnsubs();
          return; // Skip normal error handling
        }

        if (chatId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.sender === "bot" && String(m.chat_id) === String(chatId)
                ? appendPatch(m)
                : m,
            ),
          );
        } else if (botTypingId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === botTypingId ? appendPatch(m) : m)),
          );
        }
        isTypingRef.current = false;
        setIsBotResponding(false);
        setBotStatus("idle");
        dispatch(setRefreshSidebar(true));
        cancelScheduledCleanup();
        clearActiveStreamState(streamSessionId);
        clearStreamUnsubs();
      });

      const unsubBlocked = stream.on("blocked", (data) => {
        chatStreamDebugLog("sse_blocked", {
          session_id: data?.session_id || streamSessionId,
          chat_id: stream.chatId,
          error: data?.error || null,
        });
        setTokenUsageInfoSafely(data?.token_usage || null);
        setUsageCycleInfoSafely(data?.usage_cycle || null);
        setSessionLimitBlocked(true);
        writeStoredSessionBlocked(data?.session_id || streamSessionId, true);
        isTypingRef.current = false;
        setIsBotResponding(false);
        setBotStatus("idle");

        const message = data?.message || "Session limit reached.";
        const chatId = stream.chatId;
        const latestTrace = getStreamTrace();

        const finalizePatch = (m) => {
          const currentText = (m.message || m.partialMessage || "").trim();
          return {
            ...m,
            message: currentText, // 🚫 Do NOT append the error message
            partialMessage: "",
            showDots: false,
            streamingStatus: "",
            response_status: "fail",
            executionTrace: latestTrace,
            streamSteps: latestTrace?.steps || [],
          };
        };

        let hasContent = false;
        if (chatId) {
          const target = messagesRef.current.find(
            (m) => m.sender === "bot" && String(m.chat_id) === String(chatId),
          );
          if (
            target &&
            (target.message || target.partialMessage || "").trim()
          ) {
            hasContent = true;
          }
        } else if (botTypingId) {
          const target = messagesRef.current.find((m) => m.id === botTypingId);
          if (
            target &&
            (target.message || target.partialMessage || "").trim()
          ) {
            hasContent = true;
          }
        }

        if (hasContent) {
          if (chatId) {
            setMessages((prev) =>
              prev.map((m) =>
                m.sender === "bot" && String(m.chat_id) === String(chatId)
                  ? finalizePatch(m)
                  : m,
              ),
            );
          } else if (botTypingId) {
            setMessages((prev) =>
              prev.map((m) => (m.id === botTypingId ? finalizePatch(m) : m)),
            );
          }

          // 💾 Persist the partial text to the DB if we have a chatId
          if (chatId) {
            const target = messagesRef.current.find(
              (m) => m.sender === "bot" && String(m.chat_id) === String(chatId),
            );
            const finalBotText = target ? finalizePatch(target).message : "";
            api
              .post(`${process.env.REACT_APP_API_URL}/chat/stop/${chatId}`, {
                bot_response: finalBotText,
                response_status: "fail",
              })
              .catch(() => {});
          }
        } else {
          // Remove the empty bubble entirely
          if (chatId) {
            setMessages((prev) =>
              prev.filter(
                (m) =>
                  !(m.sender === "bot" && String(m.chat_id) === String(chatId)),
              ),
            );
          } else if (botTypingId) {
            setMessages((prev) => prev.filter((m) => m.id !== botTypingId));
          }

          if (chatId) {
            api
              .post(`${process.env.REACT_APP_API_URL}/chat/stop/${chatId}`, {
                bot_response: "",
                response_status: "fail",
              })
              .catch(() => {});
          }
        }

        handleSessionLimitReached(message);

        cancelScheduledCleanup();
        clearActiveStreamState(streamSessionId);
        clearStreamUnsubs();
      });

      streamUnsubsRef.current = [
        unsubMeta,
        unsubActivity,
        unsubStreamEvent,
        unsubStep,
        unsubChunk,
        unsubFirstToken,
        unsubDone,
        unsubStop,
        unsubError,
        unsubBlocked,
      ];
    },
    [
      clearActiveStreamState,
      chatStreamDebugLog,
      clearStreamUnsubs,
      dispatch,
      getBotGeneratedFileMeta,
      navigate,
      normalizeSessionId,
      sameId,
      setTokenUsageInfoSafely,
      setUsageCycleInfoSafely,
      syncProfilesFromResponse,
      updateResponseRecordTime,
      writeActiveStreamState,
      writeStoredSessionBlocked,
    ],
  );

  const handleSend = useCallback(
    async (customInput, filesParam = null) => {
      // Prevent multiple concurrent executions of handleSend() across all entry points.
      // (Send button, Enter key, auto-send, retry, related questions, etc.)
      if (sendingRef.current) {
        return;
      }
      sendingRef.current = true;

      try {
        let finalInput = customInput ?? input;

        const modelToUse = selectedModelRef.current;

        if (sessionId && isSessionStatusChecking) {
          return;
        }

        if (sessionLimitBlocked) {
          handleSessionLimitReached(
            "Session limit reached. Please start a new session to continue.",
          );
          return;
        }

        if (!modelToUse) {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "warning",
            title: "Please select a model before sending a message.",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
          return;
        }

        // console.log("Selected Model", modelToUse);

        const filesToUse = filesParam ?? attachedFiles;

        const getExt = (name) => name?.split(".").pop()?.toLowerCase();
        const doneFiles = filesToUse.filter((f) =>
          ["done", "already_trained"].includes(f.status),
        );
        const emailFiles = doneFiles.filter((f) => {
          const ext = getExt(f?.file?.name);
          return ext === "zip" || ext === "msg";
        });

        if (!finalInput.trim() && emailFiles.length > 0) {
          finalInput = "Files uploaded for expense email processing.";
        }

        const attached_document = filesToUse
          .filter((f) => ["done", "already_trained"].includes(f.status))
          .map((f) => ({
            name: f.file.name,
            size: f.file.size,
            type: f.file.type || getExt(f.file.name) || "unknown",
            blob: f.file, // enable in-app preview for newly attached files
          }));

        const document_ids = (() => {
          const ids = uploadedDocumentIds || {};
          const out = {};
          (uploadedDocNames || []).forEach((name) => {
            const key = String(name || "")
              .trim()
              .toLowerCase();
            const docId = ids[key];
            if (key && docId) out[key] = docId;
          });
          return out;
        })();

        if (!finalInput.trim() && filesToUse.length === 0) return;
        if (!finalInput.trim()) return;

        if (!token) {
          Swal.fire({
            icon: "error",
            title: "Unauthorized",
            text: "Please login again.",
          });
          return;
        }

        clearRecovery();

        // Ensure we have a session id so we can recover from request drops by
        // re-fetching history without requiring a manual page refresh.
        const ensuredSessionId = await ensureSessionForSend(modelToUse);
        const effectiveSessionId = ensuredSessionId || sessionId || null;
        const client_message_id = makeClientMessageId();

        if (effectiveSessionId && client_message_id) {
          chatStreamDebugLog("send_start", {
            session_id: effectiveSessionId,
            client_message_id: String(client_message_id),
          });
          writeActiveStreamState(effectiveSessionId, {
            session_id: effectiveSessionId,
            client_message_id: String(client_message_id),
            lastLen: 0,
          });
        }

        // 🔐 LOCK BOT
        setBotStatus("requesting");
        botStartTimeRef.current = Date.now();

        // ---------------- USER MESSAGE ----------------
        const time = new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        const filesForMessage = filesToUse
          .filter((f) => ["done", "already_trained"].includes(f.status))
          .map((f) => ({
            name: f.file.name,
            size: f.file.size,
            type: f.file.type || getExt(f.file.name) || "unknown",
          }));

        const userMsg = {
          id: Date.now(),
          sender: "user",
          message: finalInput,
          files: filesForMessage,
          time,
          client_message_id,
          userName: user?.user_name || userName || "User",
          userPic: profilePic || loggedInPic,
        };

        setMessages((prev) => [...prev, userMsg]);
        setShouldForceScroll(true);
        setInput("");
        setAttachedFiles([]);
        resizeInputTextarea(true);

        // ---------------- BOT TYPING ----------------
        const botStartedAt = Date.now();
        const botTypingId = botStartedAt + 1;
        setMessages((prev) => [
          ...prev,
          {
            id: botTypingId,
            sender: "bot",
            message: "",
            partialMessage: "",
            showDots: true,
            time,
            client_message_id,

            // ⏱️ ADD HERE
            startedAt: botStartedAt,
            liveResponseTime: 0,

            // 🔹 NEW (initialize)
            response_time: null,
            input_tokens: null,
            output_tokens: null,
            executionTrace: createEmptyExecutionTrace(),
            streamSteps: [],
          },
        ]);
        // 🔐 BOT BUSY
        setBotStatus("requesting");
        setIsBotResponding(true);

        // Track the placeholder row immediately so Stop works even before the
        // first meta/chunk arrives.
        activeResponseRef.current = {
          botTypingId,
          chatId: null,
          sessionId: effectiveSessionId,
          clientMessageId: client_message_id ? String(client_message_id) : null,
          fullResponse: "",
          charIndex: 0,
        };

        const hasDocs = uploadedDocNames.length > 0 || emailFiles.length > 0;
        const knowledgeEnabled = Boolean(
          isKnowledgeSearchActive ||
          selectedGroups.length > 0 ||
          selectedFiles.length > 0 ||
          selectedKnowledgeContexts.length > 0,
        );

        chatStreamDebugLog("send_context", {
          session_id: effectiveSessionId,
          client_message_id: String(client_message_id || ""),
          has_docs: Boolean(hasDocs),
          knowledge_enabled: Boolean(knowledgeEnabled),
          email_files_count: Array.isArray(emailFiles) ? emailFiles.length : 0,
        });

        try {
          const apiEndpoint = isFullPrivacy
            ? `${process.env.REACT_APP_API_URL}/chat/chatroute`
            : `${process.env.REACT_APP_API_URL}/chat/chatroute`;

          // End-to-end mode encrypts chat message payloads on the client.
          // Standard/None modes send plaintext.
          const securePrompt = await encryptOutgoingChatText(finalInput);

          const requestBody = isFullPrivacy
            ? {
                prompt: securePrompt,
                model_id: modelToUse,
                session_id: effectiveSessionId,
                mode: selectedMode,
                attached_document,
                client_message_id,
                // search_active: searchActive, // ✅ here
                search_active: isSearchActive,
                // Global KB toggle only (Custom RAG is inferred from selected files/groups)
                knowledge_search_active: isKnowledgeSearchActive,
                knowledge_search_enabled: isKnowledgeSearchActive,
                group_ids: selectedGroups,
                knowledge_base_files: selectedFiles,
                knowledge_contexts: selectedKnowledgeContexts,
                has_docs: hasDocs,
                file_names: uploadedDocNames,
                document_ids,
                // ✅ NEW: AI preferences
                preferences: {
                  tone: aiPreferences.tone,
                  response_length: aiPreferences.length,
                  ai_role: aiPreferences.aiRole,
                  user_role: aiPreferences.userRole,
                },
              }
            : {
                prompt: securePrompt,
                model_id: modelToUse,
                session_id: effectiveSessionId,
                mode: selectedMode,
                attached_document,
                client_message_id,
                // search_active: searchActive, // ✅ here
                search_active: isSearchActive,
                // Global KB toggle only (Custom RAG is inferred from selected files/groups)
                knowledge_search_active: isKnowledgeSearchActive,
                knowledge_search_enabled: isKnowledgeSearchActive,
                group_ids: selectedGroups,
                knowledge_base_files: selectedFiles,
                knowledge_contexts: selectedKnowledgeContexts,
                has_docs: hasDocs,
                file_names: uploadedDocNames,
                document_ids,
                // ✅ NEW: AI preferences
                preferences: {
                  tone: aiPreferences.tone,
                  response_length: aiPreferences.length,
                  ai_role: aiPreferences.aiRole,
                  user_role: aiPreferences.userRole,
                },
              };

          // const apiEndpoint = isFullPrivacy ? "/chat/save" : "/chat/query";

          // const requestBody = isFullPrivacy
          //   ? {
          //       prompt: finalInput,
          //       model_name: selectedModel,
          //       session_id: sessionId,
          //       mode: selectedMode,
          //       attached_document,
          //     }
          //   : {
          //       query: finalInput,
          //       model_name: selectedModel,
          //       session_id: sessionId,
          //       mode: selectedMode,
          //       attached_document,
          //       search_active: searchActive, // ✅ here
          //     };

          // ✅ AXIOS INSTANCE (REFRESH TOKEN SAFE)
          // ✅ TOKEN ADDED HERE (ONLY CHANGE)

          const useMultipart = emailFiles.length > 0;

          let payloadToSend = requestBody;
          if (useMultipart) {
            const formData = new FormData();
            formData.append("prompt", requestBody.prompt ?? "");
            formData.append("model_id", requestBody.model_id ?? "");

            if (requestBody.session_id != null) {
              formData.append("session_id", requestBody.session_id);
            }
            if (requestBody.mode != null) {
              formData.append("mode", requestBody.mode);
            }

            formData.append(
              "client_message_id",
              String(requestBody.client_message_id || ""),
            );

            formData.append(
              "search_active",
              String(Boolean(requestBody.search_active)),
            );
            formData.append(
              "knowledge_search_active",
              String(Boolean(requestBody.knowledge_search_active)),
            );
            formData.append(
              "knowledge_search_enabled",
              String(Boolean(requestBody.knowledge_search_enabled)),
            );
            formData.append(
              "group_ids",
              JSON.stringify(requestBody.group_ids || []),
            );
            formData.append(
              "knowledge_base_files",
              JSON.stringify(requestBody.knowledge_base_files || []),
            );
            formData.append(
              "knowledge_contexts",
              JSON.stringify(requestBody.knowledge_contexts || []),
            );
            formData.append("has_docs", String(Boolean(requestBody.has_docs)));

            formData.append(
              "file_names",
              JSON.stringify(requestBody.file_names || []),
            );
            formData.append(
              "document_ids",
              JSON.stringify(requestBody.document_ids || {}),
            );
            formData.append(
              "attached_document",
              JSON.stringify(requestBody.attached_document || []),
            );
            formData.append(
              "preferences",
              JSON.stringify(requestBody.preferences || {}),
            );

            const zipFile = emailFiles.find(
              (f) => getExt(f?.file?.name) === "zip",
            )?.file;
            if (zipFile) formData.append("zipfile", zipFile);

            emailFiles
              .filter((f) => getExt(f?.file?.name) === "msg")
              .forEach((f) => formData.append("msgfiles", f.file));

            payloadToSend = formData;
          }

          const streamAttempt = await streamManager.startSessionStream({
            sessionId: effectiveSessionId,
            url: apiEndpoint,
            payload: payloadToSend,
            accessToken: token,
            startedAtMs: botStartedAt,
          });
          if (streamAttempt?.usedStream && streamAttempt.stream) {
            clearStreamUnsubs();
            bindManagedStreamToUi({
              stream: streamAttempt.stream,
              botTypingId,
              currentSessionId: effectiveSessionId,
            });
            setUploadedDocNames([]);
            return;
          }

          if (streamAttempt?.sessionBlocked) {
            setTokenUsageInfoSafely(streamAttempt?.token_usage || null);
            setUsageCycleInfoSafely(streamAttempt?.usage_cycle || null);
            setSessionLimitBlocked(true);
            writeStoredSessionBlocked(
              streamAttempt?.session_id || effectiveSessionId,
              true,
            );
            clearStreamUnsubs();
            isTypingRef.current = false;
            setIsBotResponding(false);
            setBotStatus("idle");

            // Remove the empty bubble entirely since no stream started
            setMessages((prev) => prev.filter((m) => m.id !== botTypingId));

            handleSessionLimitReached(
              streamAttempt?.message ||
                "Session limit reached. Please start a new session to continue.",
            );
            return;
          }

          // Prefer async job API (fast 202 + poll result) only when streaming
          // is not available/usable for this browser/session.
          try {
            const jobResponse = await api.post(
              `${process.env.REACT_APP_API_URL}/chat/job`,
              payloadToSend,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            const jobData = jobResponse?.data;
            if (jobResponse?.status === 202 && jobData?.chat_id) {
              recoverBotResponseByChatId({
                chatId: jobData.chat_id,
                targetSessionId: effectiveSessionId,
                userText: finalInput,
                botTypingId,
                startedAt: botStartTimeRef.current,
              });
              return;
            }
          } catch {
            // ignore and fall back
          }

          const response = await api.post(apiEndpoint, payloadToSend, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const botData = response.data;

          const tokenUsage = extractTokenUsage(botData);
          if (tokenUsage) {
            setTokenUsageInfoSafely(tokenUsage || botData?.token_usage || null);
          }
          const usageCycle = extractUsageCycle(botData);
          if (usageCycle) {
            setUsageCycleInfoSafely(usageCycle);
          }
          const reached =
            isSessionLimitError(botData) ||
            (tokenUsage && isSessionLimitReached(tokenUsage));
          if (reached) {
            setTokenUsageInfoSafely(tokenUsage || botData?.token_usage || null);
            if (usageCycle) {
              setUsageCycleInfoSafely(usageCycle);
            }
            setSessionLimitBlocked(true);
            writeStoredSessionBlocked(
              botData?.session_id || effectiveSessionId,
              true,
            );
            clearStreamUnsubs();
            isTypingRef.current = false;
            setIsBotResponding(false);
            setBotStatus("idle");

            // Remove the empty bubble entirely since the response was blocked
            setMessages((prev) => prev.filter((m) => m.id !== botTypingId));

            handleSessionLimitReached(getSessionLimitMessage(botData));
            return;
          }

          maybeShowModelSuggestionToast(botData, {
            chatId: botData?.chat_id || null,
            sessionId: botData?.session_id || effectiveSessionId,
            clientMessageId: client_message_id || null,
          });

          const encryptionModeFromServer =
            botData?.chat_encryption_mode || null;

          let fullResponse =
            botData.response ||
            botData.message ||
            "Apologies, there was a temporary issue processing your request.";

          fullResponse = await decryptIncomingChatText(fullResponse, {
            modeOverride: encryptionModeFromServer,
          });

          // console.log("Bot Data:", botData, "session ID", sessionId);

          if (botData.session_id && !sameId(botData.session_id, sessionId)) {
            skipHistoryFetchRef.current = true; // 🚫 SKIP HISTORY
            const normalized = normalizeSessionId(botData.session_id);
            setSessionId(normalized);
            dispatch(setReduxSessionId(normalized));
            navigate("/user/new", {
              state: { sessionId: normalized },
              replace: true,
            });
          }

          dispatch(setRefreshSidebar(true));
          startTypingAnimation(fullResponse, botTypingId, botData);
          setUploadedDocNames([]);
          // setBotStatus("idle");
        } catch (error) {
          console.error("Chat API Error:", error);
          // setUploadedDocNames([]);

          // 🛑 Stop typing animation
          if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }

          // If the request drops (timeout / network), the backend may still finish
          // and save the response. Try to recover via history fetch before showing
          // a final apology.
          isTypingRef.current = false;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message: "Response stopped.",
                    partialMessage: "",
                    showDots: true,
                  }
                : msg,
            ),
          );

          if (effectiveSessionId) {
            recoverBotResponse({
              targetSessionId: effectiveSessionId,
              userText: finalInput,
              botTypingId,
              startedAt: botStartTimeRef.current,
            });
          } else {
            // No session id available; fall back to previous behavior.
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === botTypingId
                  ? {
                      ...msg,
                      message:
                        "We apologize for the inconvenience. We’re temporarily unable to process your request due to a service issue on our end. Please try again soon.",
                      partialMessage: "",
                      showDots: false,
                      response_record_time:
                        msg.liveResponseTime?.toFixed(2) ?? "0.00",
                    }
                  : msg,
              ),
            );
            setIsBotResponding(false);
            setBotStatus("idle");
            dispatch(setRefreshSidebar(true));
          }
        }
      } finally {
        sendingRef.current = false;
      }
    },
    [
      input,
      selectedModel,
      attachedFiles,
      token,
      user,
      userName,
      profilePic,
      loggedInPic,
      isFullPrivacy,
      sessionId,
      selectedMode,
      selectedGroups,
      selectedFiles,
      selectedKnowledgeContexts,
      isKnowledgeSearchActive,
      isSearchActive,
      uploadedDocNames,
      uploadedDocumentIds,
      aiPreferences,
      navigate,
      dispatch,
      startTypingAnimation,
      clearRecovery,
      ensureSessionForSend,
      recoverBotResponse,
      recoverBotResponseByChatId,
      makeClientMessageId,
      normalizeSessionId,
      sameId,
      streamManager,
      clearStreamUnsubs,
      bindManagedStreamToUi,
      isSessionStatusChecking,
      sessionLimitBlocked,
      chatStreamDebugLog,
      setTokenUsageInfoSafely,
      setUsageCycleInfoSafely,
      writeActiveStreamState,
      writeStoredSessionBlocked,
      handleSessionLimitReached,
      detectSessionLimitFromResponse,
    ],
  );

  // ============================================================================
  // MEMOIZED HANDLERS - Wrapped in useCallback to prevent recreation
  // ============================================================================

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast("Text copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, []);

  const isAnyBotTyping = isTypingRef.current;

  const handleRetry = useCallback(
    (botMsgId) => {
      // 🚫 HARD BLOCK
      if (isBotBusy()) {
        showErrorToast(
          "The system is currently generating a response. Please try again shortly.",
        );
        return;
      }

      const botIndex = messages.findIndex((msg) => msg.id === botMsgId);
      if (botIndex === -1) return;

      for (let i = botIndex - 1; i >= 0; i--) {
        if (messages[i].sender === "user") {
          handleSend(messages[i].message);
          break;
        }
      }
    },
    [messages, handleSend],
  );

  const handleStopResponse = useCallback(() => {
    const active = activeResponseRef.current;

    clearRecovery();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }

    const nowMs = Date.now();
    const stopRow = messagesRef.current.find(
      (m) => m?.id === active?.botTypingId,
    );
    const startedAtMs = Number(stopRow?.startedAt || 0) || 0;
    const liveSeconds = Number(stopRow?.liveResponseTime ?? 0);

    const sessionToStop = active?.sessionId || sessionId || null;
    if (sessionToStop) {
      // Stop client-side stream immediately (background streaming manager)
      streamManager.stopSessionStream(sessionToStop);
      clearActiveStreamState(sessionToStop);
    }

    isTypingRef.current = false;
    setIsBotResponding(false);
    setBotStatus("idle");

    const stoppedBeforeStartMessage =
      "Request cancelled before response generation started.";

    const stopRecordTime = (() => {
      if (startedAtMs > 0 && nowMs >= startedAtMs) {
        const elapsed = (nowMs - startedAtMs) / 1000;
        // Avoid showing 0.00 for immediate cancels.
        return Math.max(0.01, Number(elapsed.toFixed(2))).toFixed(2);
      }
      if (Number.isFinite(liveSeconds) && liveSeconds > 0) {
        return Number(liveSeconds.toFixed(2)).toFixed(2);
      }
      return "0.00";
    })();

    const visiblePartial = String(active?.fullResponse || "").slice(
      0,
      Number(active?.charIndex || 0),
    );
    const stopPersistText = visiblePartial.trim()
      ? visiblePartial
      : stoppedBeforeStartMessage;

    // Best-effort: cancel upstream Python stream by session_id (works even
    // before chat_id is known).
    if (sessionToStop) {
      api
        .post(
          `${process.env.REACT_APP_API_URL}/chat/stop-stream`,
          {
            session_id: sessionToStop,
            client_message_id: active?.clientMessageId || null,
            // Used when chat_id is not known yet (pre-meta stop).
            bot_response: stopPersistText,
            response_status: "stopped",
            response_record_time: stopRecordTime,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .catch(() => {});
    }

    if (active?.botTypingId && active?.chatId) {
      const partial = String(active.fullResponse || "").slice(
        0,
        Number(active.charIndex || 0),
      );
      const visible = partial.trim();
      const finalText = visible ? partial : stoppedBeforeStartMessage;

      // Show only the visible part (ChatGPT-like stop).
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === active.botTypingId
            ? {
                ...msg,
                message: finalText,
                partialMessage: "",
                showDots: false,
                response_status: "stopped",
                response_record_time:
                  msg.response_record_time ?? stopRecordTime,
              }
            : msg,
        ),
      );

      // Persist truncated response + mark stopped (best-effort).
      api
        .post(
          `${process.env.REACT_APP_API_URL}/chat/stop/${active.chatId}`,
          { bot_response: finalText, response_record_time: stopRecordTime },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .catch(() => {});
    } else {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === active?.botTypingId || msg.showDots
            ? {
                ...msg,
                message:
                  msg.sender === "bot" &&
                  String(msg.message || "").trim().length === 0 &&
                  String(msg.partialMessage || "").trim().length === 0
                    ? stoppedBeforeStartMessage
                    : msg.message,
                partialMessage: "",
                showDots: false,
                response_status: "stopped",
                response_record_time:
                  msg.response_record_time ?? stopRecordTime,
              }
            : msg,
        ),
      );
    }
    window.speechSynthesis.cancel();
  }, [
    api,
    clearActiveStreamState,
    clearRecovery,
    sessionId,
    streamManager,
    token,
  ]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    dispatch(setIsChatPage(true));
    return () => {
      clearRecovery();
      clearStreamUnsubs();
      dispatch(setIsChatPage(false));
    };
  }, [dispatch, clearRecovery, clearStreamUnsubs]);

  useEffect(() => {
    const incoming = normalizeSessionId(location.state?.sessionId);
    if (incoming && !sameId(incoming, sessionId)) {
      setSessionId(incoming);
    }
  }, [location.state?.sessionId, normalizeSessionId, sameId, sessionId]);

  useEffect(() => {
    if (!chatResetToken) return;

    setMessages([]);
    setInput("");
    setAttachedFiles([]);
    setUploadedDocNames([]);
    setEditingId(null);
    setIsBotResponding(false);
    setBotStatus("idle");
    setSessionLimitBlocked(false);
    setTokenUsageInfo(null);
    setUsageCycleInfo(null);
    setIsSessionStatusChecking(false);
    skipHistoryFetchRef.current = false;
    hasInteractedRef.current = false;

    // If a session id was created, set it; otherwise start fresh (null)
    setSessionId(normalizeSessionId(location.state?.sessionId));
  }, [chatResetToken]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      (firstMessage || attachedFilesFromHome?.length > 0) &&
      !hasInteractedRef.current
    ) {
      if (sessionId && (sessionLimitBlocked || isSessionStatusChecking)) return;
      handleSend(firstMessage, attachedFilesFromHome);
      hasInteractedRef.current = true;
    }
  }, [
    refreshKey,
    firstMessage,
    attachedFilesFromHome,
    handleSend,
    sessionId,
    sessionLimitBlocked,
    isSessionStatusChecking,
  ]);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (!chatBox) return;

    const isUserAtBottom =
      chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

    if (shouldForceScroll || isUserAtBottom) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShouldForceScroll(false);
    }
  }, [messages, shouldForceScroll]);

  useEffect(() => {
    if (!sessionId) {
      setSessionLimitBlocked(false);
      setTokenUsageInfo(null);
      setIsSessionStatusChecking(false);
      return;
    }

    let cancelled = false;

    // Immediately lock/unlock based on persisted state (prevents flash after refresh).
    setSessionLimitBlocked(readStoredSessionBlocked(sessionId));
    setTokenUsageInfo(null);
    setUsageCycleInfo(null);
    setIsSessionStatusChecking(true);

    // 🚫 SKIP if sessionId came from handleSend
    if (skipHistoryFetchRef.current) {
      skipHistoryFetchRef.current = false; // reset for next time
      setIsSessionStatusChecking(false);
      return;
    }

    const run = async () => {
      const showLoading = (messagesRef.current || []).length === 0;
      if (showLoading) setIsHistoryLoading(true);
      try {
        const payload = await fetchSessionPayload(sessionId, { silent: true });
        const resolved = resolveSessionBlockedFromPayload(payload || {});
        const resolvedSessionId = resolved.sessionId || sessionId;
        const sessionContexts = Array.isArray(payload?.knowledge_contexts)
          ? payload.knowledge_contexts
          : [];
        const sessionContextSummary =
          payload?.knowledge_context_summary &&
          typeof payload.knowledge_context_summary === "object"
            ? payload.knowledge_context_summary
            : {};
        const sessionKbFiles = Array.isArray(payload?.knowledge_base_files)
          ? payload.knowledge_base_files
              .map((filename) =>
                typeof filename === "string"
                  ? { filename, document_id: null }
                  : filename,
              )
              .filter(Boolean)
          : [];
        const sessionGroupIds = Array.isArray(payload?.knowledge_group_ids)
          ? payload.knowledge_group_ids
          : [];

        if (!cancelled) {
          setTokenUsageInfoSafely(resolved.tokenUsage || null);
          setUsageCycleInfoSafely(resolved.usageCycle || null);
          setSelectedKnowledgeContexts?.(sessionContexts);
          setKnowledgeContextSummary?.(sessionContextSummary);
          if (sessionKbFiles.length > 0) {
            setSelectedFiles?.(sessionKbFiles);
          } else if (sessionContexts.length === 0) {
            setSelectedFiles?.([]);
          }
          if (sessionGroupIds.length > 0) {
            setSelectedGroups?.(sessionGroupIds);
          }
          readStoredSessionBlocked(sessionId);
          const currentBlocked = Boolean(resolved.blocked);

          setSessionLimitBlocked(currentBlocked);
          if (currentBlocked) {
            writeStoredSessionBlocked(resolvedSessionId, true);
          }
          setIsSessionStatusChecking(false);
        }

        const formatted = await fetchSessionHistory(sessionId);
        if (Array.isArray(formatted)) {
          let stream = streamManager.getSessionStream(sessionId);

          // If we refreshed mid-stream, try to resume by reconnecting SSE using the
          // persisted (session_id + client_message_id) hint.
          if (!stream && token) {
            const key = String(sessionId);
            const attempted = streamResumeAttemptedRef.current;
            if (!attempted.has(key)) {
              attempted.add(key);

              const active = readActiveStreamState(sessionId);

              let derivedClientMessageId = null;
              let derivedLen = 0;
              try {
                for (let i = formatted.length - 1; i >= 0; i--) {
                  const msg = formatted[i];
                  if (!msg || msg.sender !== "bot") continue;
                  const status = String(
                    msg.response_status || "",
                  ).toLowerCase();
                  if (status !== "in progress" && status !== "pending")
                    continue;
                  derivedClientMessageId = msg.client_message_id || null;
                  const text = String(msg.partialMessage || msg.message || "");
                  derivedLen = text.length;
                  break;
                }
              } catch {}

              const clientMessageId =
                active?.client_message_id || derivedClientMessageId || null;

              if (!active?.client_message_id && clientMessageId) {
                chatStreamDebugLog("resume_derived_from_history", {
                  session_id: sessionId,
                  client_message_id: String(clientMessageId),
                  derivedLen,
                });
                writeActiveStreamState(sessionId, {
                  session_id: sessionId,
                  client_message_id: String(clientMessageId),
                  lastLen: derivedLen,
                });
              }

              if (clientMessageId) {
                try {
                  let seedText = "";
                  try {
                    for (let i = formatted.length - 1; i >= 0; i--) {
                      const msg = formatted[i];
                      if (!msg || msg.sender !== "bot") continue;
                      const status = String(
                        msg.response_status || "",
                      ).toLowerCase();
                      if (status !== "in progress" && status !== "pending")
                        continue;
                      if (
                        String(msg.client_message_id || "") !==
                        String(clientMessageId)
                      )
                        continue;
                      seedText = String(
                        msg.partialMessage || msg.message || "",
                      );
                      break;
                    }
                  } catch {}

                  const fallbackFrom = Number.isFinite(Number(active?.lastLen))
                    ? Number(active?.lastLen)
                    : derivedLen;
                  const resumeFrom = seedText ? seedText.length : fallbackFrom;

                  chatStreamDebugLog("resume_attempt", {
                    session_id: sessionId,
                    client_message_id: String(clientMessageId),
                    from: resumeFrom,
                  });
                  const resumeUrl = `${process.env.REACT_APP_API_URL}/chat/chatroute`;
                  const resumePayload = {
                    session_id: sessionId,
                    client_message_id: String(clientMessageId),
                    stream: true,
                  };

                  const resumeAttempt = await streamManager.startSessionStream({
                    sessionId,
                    url: resumeUrl,
                    payload: resumePayload,
                    accessToken: token,
                    from: resumeFrom,
                    seedText,
                  });

                  if (resumeAttempt?.usedStream && resumeAttempt.stream) {
                    chatStreamDebugLog("resume_success", {
                      session_id: sessionId,
                    });
                    stream = resumeAttempt.stream;
                  } else {
                    chatStreamDebugLog("resume_not_used", {
                      session_id: sessionId,
                      usedStream: Boolean(resumeAttempt?.usedStream),
                    });
                  }
                } catch {
                  chatStreamDebugLog("resume_failed", {
                    session_id: sessionId,
                  });
                  // ignore resume errors; history remains visible
                }
              }
            }
          }

          const snap = stream?.getSnapshot?.() || null;
          const activeHint = readActiveStreamState(sessionId);
          const hintedClientMessageId = activeHint?.client_message_id
            ? String(activeHint.client_message_id)
            : null;
          const hintedStartedAt =
            Number.isFinite(Number(activeHint?.createdAtMs)) &&
            Number(activeHint.createdAtMs) > 0
              ? Number(activeHint.createdAtMs)
              : null;

          const next = snap?.chatId
            ? formatted.map((m) =>
                m?.sender === "bot" && String(m.chat_id) === String(snap.chatId)
                  ? {
                      ...m,
                      partialMessage: snap.fullText || m.partialMessage || "",
                      showDots: false,
                      response_status: "in progress",
                    }
                  : m,
              )
            : formatted;

          const nextWithResumeTiming =
            hintedClientMessageId && hintedStartedAt
              ? next.map((m) => {
                  if (!m || m.sender !== "bot") return m;
                  const status = String(m.response_status || "").toLowerCase();
                  if (status !== "in progress" && status !== "pending")
                    return m;
                  if (
                    String(m.client_message_id || "") !== hintedClientMessageId
                  )
                    return m;

                  const messageText = String(m.message || "").trim();
                  const shouldShowDots =
                    Boolean(m.showDots) ||
                    (messageText.length === 0 && !m.partialMessage);

                  return {
                    ...m,
                    startedAt: m.startedAt || hintedStartedAt,
                    showDots: shouldShowDots,
                    liveResponseTime:
                      shouldShowDots && m.liveResponseTime == null
                        ? Number(
                            ((Date.now() - hintedStartedAt) / 1000).toFixed(2),
                          )
                        : m.liveResponseTime,
                  };
                })
              : next;

          setMessages(nextWithResumeTiming);

          clearStreamUnsubs();
          if (stream) {
            bindManagedStreamToUi({
              stream,
              botTypingId: null,
              currentSessionId: sessionId,
            });
          }
        }
      } finally {
        if (showLoading) setIsHistoryLoading(false);
        if (!cancelled) setIsSessionStatusChecking(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    sessionId,
    fetchSessionHistory,
    fetchSessionPayload,
    readActiveStreamState,
    readStoredSessionBlocked,
    resolveSessionBlockedFromPayload,
    writeActiveStreamState,
    writeStoredSessionBlocked,
    token,
    chatStreamDebugLog,
    streamManager,
    clearStreamUnsubs,
    bindManagedStreamToUi,
  ]);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (!chatBox) return;

    chatBox.addEventListener("scroll", debouncedScrollCheck);
    return () => chatBox.removeEventListener("scroll", debouncedScrollCheck);
  }, [debouncedScrollCheck]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const authToken =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/user-profile/profile`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        );
        if (!res.ok) throw new Error("Failed to fetch profile");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchProfile();
  }, [refreshKey]);

  // Lightweight "near real-time" session usage monitor while generating a response.
  // Polls the session status endpoint to pick up token_usage increases without relying
  // on every SSE chunk carrying token_usage (keeps UI responsive).
  useEffect(() => {
    if (!sessionId || !token) return;
    if (sessionLimitBlocked) return;
    if (!isBotResponding) return;

    let cancelled = false;

    const tick = async () => {
      try {
        const payload = await fetchSessionPayload(sessionId, { silent: true });
        if (cancelled) return;
        const resolved = resolveSessionBlockedFromPayload(payload || {});
        if (cancelled) return;

        if (resolved?.tokenUsage) {
          setTokenUsageInfoSafely(resolved.tokenUsage);
        }
        if (resolved?.usageCycle) {
          setUsageCycleInfoSafely(resolved.usageCycle);
        }

        if (resolved?.blocked) {
          setSessionLimitBlocked(true);
          writeStoredSessionBlocked(resolved?.sessionId || sessionId, true);
          handleSessionLimitReached(resolved?.message);
        }
      } catch {
        // ignore - best effort only
      }
    };

    tick();
    const handle = setInterval(tick, 12000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [
    fetchSessionPayload,
    handleSessionLimitReached,
    isBotResponding,
    sessionId,
    sessionLimitBlocked,
    token,
    resolveSessionBlockedFromPayload,
    writeStoredSessionBlocked,
  ]);

  // Derived state
  const isUploading = useMemo(
    () => attachedFiles.some((f) => f.status === "uploading"),
    [attachedFiles],
  );
  const handleRelatedQuestionSend = useCallback(
    (question) => {
      if (!question || isBotResponding || isAnyBotTyping) return;

      handleSend(question); // ✅ direct send
    },
    [handleSend, isBotResponding, isAnyBotTyping],
  );

  // console.log("uploadedDocNames", uploadedDocNames);

  // ============================================================================
  // RENDER
  // ============================================================================

  // local temp state
  const [localPrefs, setLocalPrefs] = useState(aiPreferences);

  useEffect(() => {
    if (showResponsesModal) {
      setLocalPrefs(aiPreferences);
    }
  }, [showResponsesModal, aiPreferences]);

  useEffect(() => {
    if (!showResponsesModal) {
      setShowInfoTooltip(false);
    }
  }, [showResponsesModal]);

  const handleChange = (key, value) => {
    setLocalPrefs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApply = () => {
    setAiPreferences(localPrefs);
    setShowResponsesModal(false);
  };

  const handleCancel = () => {
    setLocalPrefs(aiPreferences);
    setShowResponsesModal(false);
  };

  useEffect(() => {
    const close = (e) => {
      if (
        !e.target.closest(".custom-popover") &&
        !e.target.closest(".icon-image")
      ) {
        setShowResponsesModal(false);
      }
    };

    document.addEventListener("click", close, true);
    return () => document.removeEventListener("click", close, true);
  }, []);

  const LENGTH_OPTIONS = [
    { value: "long", label: "Longest (Default)" },
    { value: "deep", label: "Deep Analysis" },
    { value: "medium", label: "Medium" },
  ];

  const AI_ROLE_OPTIONS = [
    { value: "default", label: "General Assistant (Default)" },
    { value: "Brainstorm partner", label: "Brainstorm partner" },
    { value: "Career coach", label: "Career coach" },
    { value: "Chef", label: "Chef" },
    { value: "Coding coach", label: "Coding coach" },
    { value: "Editor", label: "Editor" },
    { value: "Entertainment guide", label: "Entertainment guide" },
    { value: "Fitness trainer", label: "Fitness trainer" },
    { value: "Gardener", label: "Gardener" },
    { value: "Homework helper", label: "Homework helper" },
    { value: "Language tutor", label: "Language tutor" },
    { value: "Life coach", label: "Life coach" },
    { value: "Marketer", label: "Marketer" },
    { value: "Editor", label: "Editor" },
    { value: "Product manager", label: "Product manager" },
    { value: "Public speaking coach", label: "Public speaking coach" },
    { value: "Social media copywriter", label: "Social media copywriter" },
    { value: "Storyteller", label: "Storyteller" },
    { value: "Tech support specialist", label: "Tech support specialist" },
    { value: "Teacher", label: "Teacher" },
    { value: "Translator", label: "Translator" },
    { value: "Travel guide", label: "Travel guide" },
    { value: "Trivia expert", label: "Trivia expert" },
    { value: "Writer", label: "Writer" },
  ];

  const USER_ROLE_OPTIONS = [
    { value: "default", label: "Universal (Default)" },
    { value: "Parent", label: "Parent" },
    { value: "Professional", label: "Professional" },
    { value: "Programmer", label: "Programmer" },
    { value: "Student", label: "Student" },
    { value: "Writer", label: "Writer" },
  ];

  const TONE_MODE_MAP = [
    { value: "default", label: "Neutral (Default)" },
    { value: "eli5", label: "Simple" },
    { value: "tldr", label: "Summary" },
    { value: "jargonize", label: "Professional" },
    { value: "humanize", label: "Naturally" },
    { value: "feynman", label: "Deep" },
  ];

  const DEEP_ANALYSIS_MAP = [
    { value: "deep", label: "Deep Analysis (Default)" },
    { value: "quick", label: "Quick" },
    { value: "balanced", label: "Standard" },
  ];

  const retryUpload = async (item) => {
    // Show spinner only for the selected file
    setAttachedFiles((prev) =>
      prev.map((f) =>
        f.clientId === item.clientId ? { ...f, status: "uploading" } : f,
      ),
    );

    const formData = new FormData();
    formData.append("files", item.file);
    formData.append("model", selectedModel);
    formData.append("mode", uploadMode.toLowerCase());
    formData.append("folder_id", selectedFolder.id);

    try {
      const token =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");

      const uploadEndpoint = `${process.env.REACT_APP_API_URL}/uploaded-files/newUploadv2`;

      const res = await api.post(uploadEndpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { toasts = [] } = res.data;

      // Show backend toasts
      toasts.forEach((toast) => {
        const message = formatToastMessage(toast.message);

        if (toast.type === "success") {
          showSuccessToast(message);
        } else if (toast.type === "warning") {
          showInfoToast(message);
        } else {
          showErrorToast(message);
        }
      });

      // Update only the retried file
      setAttachedFiles((prev) =>
        prev.flatMap((f) => {
          if (f.clientId !== item.clientId) return [f];

          const ext = f?.file?.name?.split(".").pop()?.toLowerCase();
          const isArchive = ext === "zip" || ext === "rar";

          const success = toasts.find(
            (t) =>
              t.type === "success" &&
              t.message.toLowerCase().includes(f.file.name.toLowerCase()),
          );

          const duplicate = toasts.find(
            (t) =>
              t.message.toLowerCase().includes(f.file.name.toLowerCase()) &&
              (t.message.toLowerCase().includes("already exists") ||
                t.message.toLowerCase().includes("pending")),
          );

          const failed = toasts.find(
            (t) =>
              t.type === "error" &&
              t.message.toLowerCase().includes(f.file.name.toLowerCase()),
          );

          // ZIP/RAR archive: replace with extracted file entries
          if (isArchive && extractedFilenames.length > 0) {
            return extractedFilenames.map((name, idx) => ({
              file: new File([], name),
              status: "done",
              clientId: `${f.clientId}-extracted-${idx}`,
            }));
          }

          if (isArchive) return [{ ...f, status: "done" }];
          if (success) return [{ ...f, status: "done" }];
          if (duplicate) return [{ ...f, status: "already_trained" }];
          if (failed) return [{ ...f, status: "error" }];

          return [f];
        }),
      );

      /* -----------------------------------------------
         🔄 ZIP/RAR retry: update uploadedDocNames with
         extracted file names
      ----------------------------------------------- */
      const archiveExtPattern = /\.(zip|rar)$/i;
      const extractedFilenames = toasts
        .map((t) => {
          const msg = t.message || "";
          const match = msg.match(
            /^(.+?)\s+(uploaded successfully|already exists|is pending|exists in)/i,
          );
          return match ? match[1].trim().toLowerCase() : null;
        })
        .filter(Boolean);

      if (extractedFilenames.length > 0) {
        setUploadedDocNames((prev) => {
          const withoutArchives = prev.filter(
            (name) => !archiveExtPattern.test(name),
          );
          return [...new Set([...withoutArchives, ...extractedFilenames])];
        });
      }
    } catch (error) {
      const backendMessage =
        error.response?.data?.message || error.response?.data?.error;

      showErrorToast(backendMessage || "Retry failed. Please try again later.");

      setAttachedFiles((prev) =>
        prev.map((f) =>
          f.clientId === item.clientId ? { ...f, status: "error" } : f,
        ),
      );
    }
  };

  return (
    <div className="w-100 h-100 overflow-hidden chat-wrapper">
      <div className="flex-grow-1  overflow-auto chat-box">
        {isHistoryLoading ? (
          <p className="text-center refresh-prompts mt-3">
            Loading messages...
          </p>
        ) : filteredMessages.length === 0 ? (
          <p className="text-center refresh-prompts mt-3">
            {isCreatingSession ? "Starting new chat..." : "No results found."}
          </p>
        ) : (
          filteredMessages.map((msg) => (
            <ChatMessage
              key={msg.id}
              msg={msg}
              loggedInPic={loggedInPic}
              userName={user?.user_name || userName}
              fontSize={fontSizeMap[chatPreferences.font_size] || "1rem"}
              onCopy={handleCopy}
              onRetry={handleRetry}
              onTextToSpeech={handleTextToSpeech}
              onRelatedQuestionSend={handleRelatedQuestionSend}
              searchActive={isSearchActive}
              // 🔹 ADD THIS
              onUpdateMessage={updateBotMessage}
              botBusy={botBusy} // ✅ ADD THIS
              selectedKbFiles={selectedFiles}
              onToggleKbFile={toggleKbFile}
              onKbNewChat={createNewChatFromKb}
              onFeedback={handleMessageFeedback}
              sessionLimitBlocked={sessionLimitBlocked}
              onOpenPreview={openPreview}
            />
          ))
        )}

        <div ref={chatEndRef} />
      </div>

      {chatPreferences.auto_scroll && showScrollDown && (
        <button
          className="scroll-down-btn m-auto"
          onClick={() =>
            chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        >
          <i className="bi bi-arrow-down" style={{ fontSize: "24px" }}></i>
        </button>
      )}

      <div className="">
        <div
          className="rounded p-1 mt-1 input-area"
          style={{ border: "1px solid var(--dark-color)" }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.doc,.pptx,.xlsx,.zip,.msg,.mp3,.wav,.aac,.flac,.mp4,.mov,.avi,.mkv,.flv,.png,.jpg,.jpeg,.gif,.bmp,.tiff"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {attachedFiles.length > 0 && (
            <div className="d-flex flex-wrap mb-2">
              {attachedFiles.map((item, idx) => (
                <div
                  key={idx}
                  className="attached-file-chip position-relative me-2 mt-1 rounded bg-light d-flex flex-column px-2 py-1"
                  style={{
                    maxWidth: "200px",
                    overflow: "visible",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    <img src={fileIcon} alt="File" width="18" />
                    <span
                      style={{
                        fontSize: "small",
                        display: "inline-block",
                        maxWidth: "180px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        verticalAlign: "middle",
                      }}
                      title={item.file.name}
                    >
                      {item.file.name}
                    </span>
                  </div>

                  <small style={{ fontSize: "10px", color: "#555" }}>
                    {item.file.size < 1024 * 1024
                      ? `${(item.file.size / 1024).toFixed(1)} KB`
                      : `${(item.file.size / (1024 * 1024)).toFixed(2)} MB`}
                  </small>

                  {item.status === "done" && (
                    <small style={{ fontSize: "10px", color: "green" }}>
                      Uploaded ✅
                    </small>
                  )}
                  {item.status === "error" && (
                    <div
                      className="d-flex justify-content-between align-items-center w-100"
                      style={{ fontSize: "10px", color: "red" }}
                    >
                      <span>Failed ❌</span>

                      <button
                        type="button"
                        className="cancle-btn p-1 ak-text-sm"
                        style={{
                          fontSize: "10px",
                          border: "none",
                          cursor: "pointer",
                        }}
                        onClick={() => retryUpload(item)}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {item.status === "already_trained" && (
                    <small style={{ fontSize: "10px", color: "green" }}>
                      Uploaded ✅{/* Training Completed ✅ */}
                    </small>
                  )}

                  <img
                    src={crossIcon}
                    alt="Remove"
                    onClick={() => removeAttachedFile(idx)}
                    style={{
                      position: "absolute",
                      top: "-2px",
                      right: "-6px",
                      width: "14px",
                      height: "14px",
                      zIndex: 1003,
                      cursor: "pointer",
                    }}
                  />

                  {item.status === "uploading" && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(255, 255, 255, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "inherit",
                        zIndex: 2,
                      }}
                    >
                      <div
                        className="spinner-border"
                        role="status"
                        style={{
                          width: "20px",
                          height: "20px",
                          color: "var(--dark-color)",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mb-2">
            <textarea
              ref={inputTextareaRef}
              className={`p-2 pb-4 border-0 shadow-none input-textarea ${
                sessionLimitBlocked ? "session-limit-blocked" : ""
              }`}
              placeholder={
                sessionLimitBlocked
                  ? "Session limit reached. Please start a new session to continue."
                  : isSessionStatusChecking
                    ? "Checking session status..."
                    : hasEmailExtractFiles
                      ? "Attachments received for email extraction. You can add more files. Text input is disabled."
                      : isFocused
                        ? ""
                        : branding(
                            "Welcome back to VelaKoda! How can I assist you?",
                          )
              }
              value={input}
              disabled={hasEmailExtractFiles || sessionLimitBlocked}
              readOnly={hasEmailExtractFiles || sessionLimitBlocked}
              onChange={(e) => {
                if (hasEmailExtractFiles || sessionLimitBlocked) return;
                setInput(e.target.value);
                resizeInputTextarea(false);
              }}
              rows={1}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={(e) => {
                if (hasEmailExtractFiles || sessionLimitBlocked) {
                  e.preventDefault();
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!isBotResponding && !isCreatingSession) {
                    handleSend();
                    resizeInputTextarea(true);
                  }
                }
              }}
            />
            {/* {sessionLimitBlocked && (
              <div
                role="alert"
                style={{
                  marginTop: "8px",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  background: "#fffbeb",
                  border: "1px solid #f59e0b",
                  color: "#92400e",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                Session limit reached. Please start a new session to continue.
              </div>
            )} */}
          </div>

          <div className="d-flex align-items-center justify-content-between rounded-3">
            <div className="d-flex gap-2">
              {/* Customize Responses */}
              <div className="position-relative">
                <button
                  className="btn p-0 chat-btn-message mb-1 border-0"
                  title="Customize Responses"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowResponsesModal((prev) => !prev);
                  }}
                >
                  <i className="bi bi-sliders fs-6"></i>
                </button>

                {showResponsesModal && (
                  <div
                    className="custom-popover ak-text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="popover-content">
                      <div className="fw-bold mb-2  ak-text-md">
                        Customize Responses
                        <span
                          className="info-icon"
                          title="Click for response option details"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowInfoTooltip((prev) => !prev);
                          }}
                          style={{
                            cursor: "pointer",
                            fontSize: "0.85em",
                            marginLeft: "6px",
                            color: "#0d6efd",
                          }}
                        >
                          (i)
                        </span>
                      </div>
                      {showInfoTooltip && (
                        <div
                          className="ak-text-xs mb-2"
                          style={{
                            color: "#000000",
                            lineHeight: 1.4,
                            padding: "10px 12px",
                            borderRadius: "8px",
                            background: "rgba(13, 110, 253, 0.06)",
                            border: "1px solid rgba(13, 110, 253, 0.18)",
                          }}
                        >
                          Tone/Style changes how the answer sounds. Length of
                          Responses adjusts verbosity. AI Role sets the
                          assistant persona. Your Role gives context for better
                          responses.
                        </div>
                      )}

                      <div className="settings-body">
                        {/* Tool Mode */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Tone / Style
                          </span>
                          <select
                            value={localPrefs.tone}
                            onChange={(e) =>
                              handleChange("tone", e.target.value)
                            }
                          >
                            {TONE_MODE_MAP.map((tone) => (
                              <option key={tone.value} value={tone.value}>
                                {tone.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Analysis */}
                        {/* <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Analysis
                          </span>
                          <select
                            value={localPrefs.length}
                            onChange={(e) =>
                              handleChange("analysis", e.target.value)
                            }
                          >
                            {DEEP_ANALYSIS_MAP.map((length) => (
                              <option key={length.value} value={length.value}>
                                {length.label}
                              </option>
                            ))}
                          </select>
                        </div> */}

                        {/* Length */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Length of Responses
                          </span>
                          <select
                            value={localPrefs.length}
                            onChange={(e) =>
                              handleChange("length", e.target.value)
                            }
                          >
                            {LENGTH_OPTIONS.map((length) => (
                              <option key={length.value} value={length.value}>
                                {length.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* AI Role */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            AI Role
                          </span>
                          <select
                            value={localPrefs.aiRole}
                            onChange={(e) =>
                              handleChange("aiRole", e.target.value)
                            }
                          >
                            {AI_ROLE_OPTIONS.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* User Role */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Your Role
                          </span>
                          <select
                            value={localPrefs.userRole}
                            onChange={(e) =>
                              handleChange("userRole", e.target.value)
                            }
                          >
                            {USER_ROLE_OPTIONS.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="d-flex gap-2 justify-content-center mt-2 align-items-center">
                        <button
                          className="cancle-btn w-25 ak-text-sm py-1"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          className="custom-btn w-25 ak-text-sm py-1"
                          onClick={handleApply}
                        >
                          Apply
                        </button>
                        <span
                          className="reset-icon"
                          title="Reset to default"
                          onClick={() => {
                            // resetAiPreferences();
                            setLocalPrefs(DEFAULT_AI_PREFERENCES);
                            // setShowResponsesModal(false);
                          }}
                        >
                          <img
                            src={reloadIcon}
                            className="icon-image pointer"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Attach */}
              <button
                className={`btn p-0 ${sessionLimitBlocked ? "disabled-btn" : "chat-btn-message"}`}
                title="Attach Files"
                onClick={handleAttachClick}
                disabled={
                  sessionLimitBlocked || isSessionStatusChecking || isUploading
                }
                style={{
                  opacity:
                    sessionLimitBlocked ||
                    isSessionStatusChecking ||
                    isUploading
                      ? 0.5
                      : 1,
                  cursor:
                    sessionLimitBlocked ||
                    isSessionStatusChecking ||
                    isUploading
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <i className="bi bi-paperclip fs-5"></i>
              </button>

              {/* Read Aloud */}
              <button
                className=" btn p-0 chat-btn-message border-0"
                onClick={toggleReadAloud}
                title="Reader"
              >
                <i
                  className={`bi ${
                    isSpeaking ? "bi-volume-mute" : "bi-volume-up"
                  } fs-5`}
                ></i>
                {/* <span className="pill-label">Reader</span> */}
              </button>

              {/* Mic */}
              <button
                className={`btn border-0 p-0 ${sessionLimitBlocked ? "disabled-btn" : "chat-btn-message"}`}
                onClick={() => {
                  setMicOpen(true);
                  startRecording();
                }}
                disabled={
                  sessionLimitBlocked || isSessionStatusChecking || isUploading
                }
                style={{
                  opacity:
                    sessionLimitBlocked ||
                    isSessionStatusChecking ||
                    isUploading
                      ? 0.5
                      : 1,
                  cursor:
                    sessionLimitBlocked ||
                    isSessionStatusChecking ||
                    isUploading
                      ? "not-allowed"
                      : "pointer",
                }}
                title="Audio"
              >
                <i className="bi bi-mic fs-5"></i>
                {/* <span className="pill-label">Audio</span> */}
              </button>

              {/* 🔴 YOUR MIC OVERLAY — UNCHANGED */}
              {micOpen && (
                <div className="mic-overlay position-absolute start-50 translate-middle-x p-3">
                  {/* Waveform */}
                  <div className="mic-waveform d-flex gap-2 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="wave-dot"></span>
                    ))}
                  </div>

                  {/* Live preview */}
                  <div
                    className="live-preview mb-2 p-2 rounded"
                    style={{
                      background: "#f8f9fa",
                      height: "40px",
                      fontSize: "12px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {isRecording
                      ? "Listening..."
                      : isProcessingSTT
                        ? "Processing..."
                        : interimTranscript}
                  </div>

                  {/* Buttons */}
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm d-flex align-items-center justify-content-center"
                      onClick={stopRecording}
                      disabled={isProcessingSTT}
                      title="Confirm"
                      style={{
                        color: "var(--btn-color)",
                        backgroundColor: "var(--primary-color)",
                        minWidth: "36px",
                        opacity: isProcessingSTT ? 0.7 : 1,
                      }}
                    >
                      {isProcessingSTT ? (
                        <span
                          className="spinner-border spinner-border-sm text-light"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="bi bi-check-lg" />
                      )}
                    </button>

                    <button
                      className="btn btn-sm border"
                      onClick={cancelRecording}
                      disabled={isProcessingSTT}
                      title="Cancel"
                      style={{
                        color: "var(--primary-color)",
                        backgroundColor: "var(--btn-color)",
                        opacity: isProcessingSTT ? 0.6 : 1,
                      }}
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="chat-send-group">
              {sessionUsageRing ? (
                <SessionUsageRing
                  percent={sessionUsageRing.percent}
                  variant={sessionUsageRing.variant}
                  title={sessionUsageRing.title}
                  size={isMobile ? 30 : 30}
                />
              ) : null}

              <button
                className="custom-send-btn m-0 p-0 d-flex align-items-center justify-content-center"
                onClick={
                  isBotResponding ? handleStopResponse : () => handleSend()
                }
                disabled={
                  isUploading ||
                  sessionLimitBlocked ||
                  isSessionStatusChecking ||
                  isCreatingSession
                }
                style={{
                  opacity:
                    isUploading ||
                    sessionLimitBlocked ||
                    isSessionStatusChecking ||
                    isCreatingSession
                      ? 0.5
                      : 1,
                  cursor:
                    isUploading ||
                    sessionLimitBlocked ||
                    isSessionStatusChecking ||
                    isCreatingSession
                      ? "not-allowed"
                      : "pointer",
                }}
                title={
                  sessionLimitBlocked
                    ? "Session limit reached. Please start a new session to continue."
                    : isBotResponding
                      ? "Stop"
                      : "Send"
                }
              >
                {isBotResponding ? (
                  <i
                    className="bi bi-stop-fill rounded-circle d-flex align-items-center justify-content-center text-white m-0 p-0"
                    title="Stop"
                    style={{
                      width: "30px",
                      height: "30px",
                      background: "#3e3e3e",
                      fontSize: "12px",
                    }}
                  />
                ) : (
                  <img
                    src={send}
                    alt="Send"
                    title="Send"
                    style={{
                      width: "30px",
                      height: "30px",
                    }}
                  />
                )}
              </button>
            </div>
          </div>
        </div>
        <span className="claimer-text d-flex align-items-center justify-content-center w-100 text-center px-0 px-md-3 py-0 py-md-1">
          VelaKoda may display inaccurate information or can make mistakes.
        </span>
      </div>
      <UploadDocumentModal
        show={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setAttachedFiles([]);
        }}
        uploadMode={uploadMode}
        setUploadMode={setUploadMode}
        uploadedInfo={uploadedInfo}
        folderItems={folderItems}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        onSubmit={handleUploadFiles}
      />

      {errorModal.show && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog-centered">
            <div className="modal-content mx-auto">
              <div className="modal-header border-bottom justify-content-center position-relative">
                <h5 className="modal-title m-0">File Size Alert</h5>
                <button
                  type="button"
                  className="modal-close-btn"
                  aria-label="Close"
                  onClick={handleCancel}
                >
                  <img src={crossIcon} className="icon-image" alt="Close" />
                </button>
              </div>
              <div className="modal-body text-center">
                <p
                  dangerouslySetInnerHTML={{ __html: errorModal.message }}
                  className="fw-normal"
                  style={{ lineHeight: "1.6", fontSize: "15px" }}
                ></p>
              </div>

              <div className="d-flex justify-content-center mb-3 gap-3">
                <button
                  type="button"
                  className="w-25 cancle-btn"
                  onClick={handleCancel}
                >
                  No
                </button>
                <button
                  type="button"
                  className="w-25 custom-btn"
                  onClick={handleOk}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
